import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { NextIndependentWork } from '../../../components/v2/student/NextIndependentWork';
import { UnitLiveInvitation } from '../../../components/v2/student/UnitLiveBridge';
import apiV2 from '../../../services/apiV2';
import { useAuth } from '../../../hooks/useAuth';
import { useAssignmentDraft, formatAssignmentRecovery } from '../../../hooks/v2/useAssignmentDraft';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { studentV2NavItems } from '../../../components/layout/v2NavItems';
import { HelpTab } from '../../../components/v2/student/HelpTab';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { hasStepContent } from '../../../hooks/v2/useHelpStepContext';
import { assignmentsApi, evidenceApi, exportApi, lessonPlansApi, sectionsApi } from '../../../services/apiV2';
import { LessonResource, LessonStep } from '../../../types/v2';
import { ExplainBroadcastContent } from '../../../components/v2/ExplainBroadcastContent';
import { StepRenderer } from '../../../components/v2/StepRenderer';
import { ResourceToolkit } from '../../../components/v2/ResourceToolkit';
import { MatchingResponseEditor, OrderingResponseEditor } from '../../../components/assessment/StructuredResponseEditors';
import { matchingPairsToTextLines, matchingRowsToPairs, matchingValueText, normalizeMatchingDraftRows } from '../../../utils/matchingPairs';
import {
  DragDropMode,
  DragDropPrompt,
  DragDropSlotValue,
  DragTokenItem,
} from './dragdrop/DragDropRenderer';

interface AssignmentDetailStep {
  step_id: number;
  title: string;
  order: number;
  stage_tag?: string | null;
  counts_as_evidence: boolean;
  step_kind: string;
  artifact_requirement?: 'none' | 'apply_required';
  artifact_location?: string;
  artifact_config?: Record<string, any>;
  content: Record<string, any>;
  signal_config?: Record<string, any>;
  accepts_response?: boolean;
  has_current_evidence?: boolean;
  latest_evidence?: {
    id: number;
    status: string;
    evidence_type: string;
    content: Record<string, any>;
    submitted_at: string;
  } | null;
  has_response?: boolean;
  latest_response?: {
    id: number;
    response: Record<string, any>;
    submitted_at: string;
    is_correct?: boolean;
    teacher_review?: { status: 'reviewed' | 'needs_revision'; notes: string };
  } | null;
  answer_key?: Record<string, any>;
  answer_key_lines?: string[];
  explanation_supported?: boolean;
  ai_thread_preview?: AssignmentAiThreadPreview | null;
}

interface AssignmentAiThreadMessage {
  id: number;
  parent_id?: number | null;
  action_type: 'missed_answer_explanation' | 'missed_answer_more_detail' | 'missed_answer_followup';
  request_text?: string;
  response_style?: 'structured_explanation' | 'chat_reply';
  content_md?: string;
  explanation_md: string;
  created_at?: string | null;
}

interface AssignmentAiThreadPreview {
  root_event_id: number;
  response_fingerprint?: string;
  messages: AssignmentAiThreadMessage[];
}

interface ReplayProgress {
  total_steps: number;
  answered_count: number;
  scorable_count: number;
  correct_count: number;
  incorrect_count: number;
  score_percent: number | null;
  current_step_id: number | null;
  review_available: boolean;
  missed_steps: Array<{
    step_id: number;
    title: string;
    order: number;
    student_response: Record<string, any>;
    student_response_text?: string;
    answer_key: Record<string, any> | null;
    answer_key_lines?: string[];
    explanation_supported?: boolean;
    ai_thread_preview?: AssignmentAiThreadPreview | null;
  }>;
  required_artifact_count?: number;
  uploaded_artifact_count?: number;
  missing_artifact_step_ids?: number[];
}

interface ReplayReview {
  available: boolean;
  score_percent: number | null;
  correct_count: number;
  incorrect_count: number;
  scorable_count: number;
  missed_steps: Array<{
    step_id: number;
    title: string;
    order: number;
    student_response: Record<string, any>;
    student_response_text?: string;
    answer_key: Record<string, any> | null;
    answer_key_lines?: string[];
    explanation_supported?: boolean;
    ai_thread_preview?: AssignmentAiThreadPreview | null;
  }>;
}

interface AssignmentDetailResponse {
  id: number;
  lesson_plan: number;
  section: number;
  assigned_at: string;
  due_at: string | null;
  run_id?: number | null;
  run_is_test_mode?: boolean;
  delivery_mode?: 'evidence_only' | 'replay_exam' | 'replay_lesson';
  purpose?: 'makeup' | 'assessment' | 'practice';
  lesson_plan_title?: string;
  section_name?: string;
  assessment_stage?: 'define' | 'do' | 'explain' | 'apply' | string;
  standards_snapshot?: Array<string | Record<string, unknown>>;
  external_next_url?: string;
  student_status?: 'not_started' | 'in_progress' | 'completed' | 'excused';
  makeup_review?: { status: 'pending' | 'needs_revision' | 'approved'; note: string } | null;
  attempt_number?: number;
  can_retake?: boolean;
  assessment_settings?: {
    show_objective_results: boolean;
    allow_ai_guidance: boolean;
  };
  retake_workflow?: {
    policy: 'automatic' | 'teacher_approval' | 'disabled';
    remediation_instructions: string;
    remediation_required: boolean;
    can_request: boolean;
    can_start: boolean;
    current_attempt_context?: {
      teacher_note: string;
      opened_by: string;
      opened_at: string | null;
    } | null;
    request: {
      id: number;
      status: 'requested' | 'approved' | 'denied' | 'used';
      remediation_response: string;
      required_remediation: string[];
      teacher_note: string;
      requested_at: string;
      decided_by: string;
      decided_at: string | null;
    } | null;
  };
  resources?: LessonResource[];
  steps: AssignmentDetailStep[];
  progress?: ReplayProgress;
  review?: ReplayReview;
  attachments?: Array<{
    id: number;
    original_name: string;
    content_type: string;
    size_bytes: number;
    download_url: string;
  }>;
  verification?: {
    teacher_feedback: string;
    evidence_summary: string;
    verified_by: string;
    verified_at: string;
  } | null;
  external_submission?: {
    confirmed: boolean;
    confirmed_at: string | null;
  };
}

type StepScoreState = 'hidden' | 'unanswered' | 'correct' | 'incorrect' | 'saved_unscored';

interface GuidedSequenceCard {
  key: string;
  prompt: string;
  input_type: 'text' | 'textarea' | 'select';
  required: boolean;
  placeholder: string;
  template: string;
  options: Array<{ value: string; label: string }>;
  validation: Record<string, any>;
}

interface PreviousAnswersItem {
  step_id: number;
  label: string;
  step_title: string;
  response_text: string;
  submitted_at?: string | null;
}

interface PreviousAnswersPayload {
  mode: string;
  title: string;
  display: string;
  allow_insert: boolean;
  items: PreviousAnswersItem[];
}

const normalizeSignalType = (step: AssignmentDetailStep): string => {
  const config = step.signal_config || {};
  return String(config.signal_type || config.type || '').trim().toLowerCase().replace(/-/g, '_');
};

const stepAcceptsReplayResponse = (step: AssignmentDetailStep): boolean => {
  if (typeof step.accepts_response === 'boolean') return step.accepts_response;
  const signalConfig = step.signal_config || {};
  if (signalConfig && Object.keys(signalConfig).length > 0) return true;
  const sourceItem = getSourceItem(step);
  if (Object.keys(sourceItem).length > 0) return true;
  const content = step.content || {};
  const rawOptions = content.options || content.choices || [];
  return Array.isArray(rawOptions) && rawOptions.some((item: any) => String(item ?? '').trim().length > 0);
};

const getObservationConfig = (step: AssignmentDetailStep): { kind: 'hands_on' | 'verbal'; skillLabel: string } | null => {
  const raw = step.content?.observation_config;
  if (!raw || typeof raw !== 'object') return null;
  const kind = raw.kind === 'verbal' ? 'verbal' : raw.kind === 'hands_on' ? 'hands_on' : null;
  if (!kind) return null;
  return {
    kind,
    skillLabel: String(raw.skill_label || '').trim(),
  };
};

const getStudentStepTitle = (step: AssignmentDetailStep): string => {
  const observation = getObservationConfig(step);
  if (!observation) return step.title || 'Untitled step';
  const label = observation.kind === 'verbal' ? 'Verbal demonstration' : 'Teacher-observed skill';
  return observation.skillLabel ? `${label}: ${observation.skillLabel}` : label;
};

const getOptions = (step: AssignmentDetailStep): string[] => {
  const config = step.signal_config || {};
  const content = step.content || {};
  const raw = config.options || config.choices || content.options || content.choices || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      if (typeof item === 'string') return item.trim();
      if (typeof item === 'object' && item !== null) {
        if (typeof item.text === 'string') return item.text.trim();
        if (typeof item.label === 'string') return item.label.trim();
        if (typeof item.value === 'string') return item.value.trim();
      }
      return String(item ?? '').trim();
    })
    .filter((item: string) => item.length > 0);
};

const getSignalPrompt = (step: AssignmentDetailStep): string => {
  const config = step.signal_config || {};
  if (typeof config.prompt === 'string' && config.prompt.trim()) return config.prompt.trim();
  if (typeof config.question === 'string' && config.question.trim()) return config.question.trim();
  return '';
};

const getGuidedSequenceCards = (step: AssignmentDetailStep): GuidedSequenceCard[] => {
  const signalConfig = step.signal_config || {};
  const signalType = String(signalConfig.signal_type || signalConfig.type || '').trim().toLowerCase().replace(/-/g, '_');
  if (signalType !== 'guided_sequence') return [];
  const cardsRaw = Array.isArray(signalConfig.cards) ? signalConfig.cards : [];
  return cardsRaw
    .map((card: any, idx: number) => {
      if (!card || typeof card !== 'object') return null;
      const key = String(card.key || '').trim();
      if (!key) return null;
      const prompt = String(card.prompt || `Prompt ${idx + 1}`).trim();
      const inputTypeRaw = String(card.input_type || 'text').trim().toLowerCase();
      const input_type = inputTypeRaw === 'textarea' || inputTypeRaw === 'select' ? inputTypeRaw : 'text';
      const required = Boolean(card.required);
      const placeholder = typeof card.placeholder === 'string' ? card.placeholder : '';
      const template = typeof card.template === 'string' ? card.template : '';
      const options = (Array.isArray(card.options) ? card.options : [])
        .map((option: any) => {
          if (option && typeof option === 'object') {
            const value = String(option.value ?? '').trim();
            const label = String(option.label ?? option.value ?? '').trim();
            return value && label ? { value, label } : null;
          }
          const value = String(option ?? '').trim();
          return value ? { value, label: value } : null;
        })
        .filter((option: { value: string; label: string } | null): option is { value: string; label: string } => Boolean(option));
      const validation = card.validation && typeof card.validation === 'object' && !Array.isArray(card.validation)
        ? card.validation
        : {};
      return {
        key,
        prompt,
        input_type,
        required,
        placeholder,
        template,
        options,
        validation,
      } satisfies GuidedSequenceCard;
    })
    .filter((card: GuidedSequenceCard | null): card is GuidedSequenceCard => Boolean(card));
};

const formatMcqAnswerOption = (options: string[], index: number): string => {
  if (!Number.isInteger(index) || index < 0) return '';
  const optionLabel = index < 26 ? String.fromCharCode(65 + index) : `#${index + 1}`;
  const optionText = String(options[index] ?? '').trim();
  if (optionText) return `${optionLabel}. ${optionText}`;
  return `${optionLabel}. Choice ${index + 1}`;
};

const getAnswerRevealLines = (step: AssignmentDetailStep): string[] => {
  const answerKey = step.answer_key;
  if (!answerKey || typeof answerKey !== 'object' || Array.isArray(answerKey)) return [];
  const kind = String(answerKey.kind || '').trim().toLowerCase();

  if (kind === 'mcq_single') {
    const options = normalizeStringList(answerKey.options);
    const correctIndex = Number(answerKey.correct_index);
    if (!Number.isInteger(correctIndex) || correctIndex < 0) return [];
    return [formatMcqAnswerOption(options, correctIndex)];
  }

  if (kind === 'mcq_multi') {
    const options = normalizeStringList(answerKey.options);
    const correctIndices = normalizeStringList(answerKey.correct_indices)
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item >= 0);
    if (correctIndices.length === 0) return [];
    return correctIndices.map((index) => formatMcqAnswerOption(options, index));
  }

  if (kind === 'ordering') {
    const expected = normalizeStringList(answerKey.answer);
    return expected.map((item, idx) => `${idx + 1}. ${item}`);
  }

  if (kind === 'matching') {
    return matchingPairsToTextLines(answerKey.pairs);
  }

  if (kind === 'text') {
    const answerCode = String(answerKey.answer_code || '').trim();
    return answerCode ? [answerCode] : [];
  }

  return [];
};

const getSourceItem = (step: AssignmentDetailStep): Record<string, any> => {
  const content = step.content || {};
  const raw = content.source_item ?? content.sourceItem ?? {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw;
};

const getSourceType = (step: AssignmentDetailStep): string => (
  String(getSourceItem(step).type || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
);

type DragDropMeta = {
  sourceType: string;
  mode: DragDropMode;
  promptLines: string[];
  bankTokens: string[];
  target: string;
  segments: string[];
  blankCount: number;
  hasBlanksInTarget: boolean;
};

const getDragDropMeta = (step: AssignmentDetailStep): DragDropMeta => {
  const sourceItem = getSourceItem(step);
  const sourceType = getSourceType(step);
  const bankTokens = (
    Array.isArray(sourceItem.tokens)
      ? sourceItem.tokens
      : (Array.isArray(sourceItem.options)
        ? sourceItem.options
        : (Array.isArray(sourceItem.token_bank) ? sourceItem.token_bank : []))
  )
    .map((item: any) => String(item ?? '').trim())
    .filter((item: string) => item.length > 0);
  const target = String(sourceItem.target || sourceItem.code || sourceItem.template || '');
  const promptLines = Array.isArray(sourceItem.prompt_lines)
    ? sourceItem.prompt_lines.map((line: any) => String(line ?? ''))
    : target
      .split('\n')
      .map((line: string) => line.replace(/\r/g, ''))
      .filter((line: string) => line.trim().length > 0);
  const segments = target ? target.split('____') : [];
  const targetBlankCount = Math.max(segments.length - 1, 0);
  const explicitBlankCountRaw = Number(sourceItem.blanks);
  const explicitBlankCount = Number.isInteger(explicitBlankCountRaw) && explicitBlankCountRaw > 0 ? explicitBlankCountRaw : 0;
  const answerTokenBlankCount = Array.isArray(sourceItem.answer_tokens) ? sourceItem.answer_tokens.length : 0;
  const tokenBankBlankCount = bankTokens.length;
  const blankCount = explicitBlankCount > 0
    ? explicitBlankCount
    : answerTokenBlankCount > 0
      ? answerTokenBlankCount
      : targetBlankCount > 0
        ? targetBlankCount
        : tokenBankBlankCount;
  const hasBlanksInTarget = Boolean(target && target.includes('____'));
  const isOrderingLine = !hasBlanksInTarget && target.trim().length > 0;
  const mode: DragDropMode = (() => {
    if (isOrderingLine) return 'code_order_line';
    if (sourceType === 'drag_drop_code') return 'code_inline';
    if (promptLines.length > 1 && blankCount >= promptLines.length) return 'row_match';
    return 'text_inline';
  })();
  return {
    sourceType,
    mode,
    promptLines,
    bankTokens,
    target,
    segments,
    blankCount,
    hasBlanksInTarget,
  };
};

const joinCodeTokens = (tokens: string[], prefix = ''): string => {
  const compactAfterPrevious = new Set(['(', '[', '{', '.']);
  const compactBeforeCurrent = new Set(['(', ')', '[', ']', '{', '}', ',', '.', ':', ';']);
  let rendered = String(prefix || '').trim();
  let previousToken = rendered ? rendered.slice(-1) : '';
  tokens.forEach((rawToken) => {
    const token = String(rawToken ?? '').trim();
    if (!token) return;
    if (!rendered) {
      rendered = token;
    } else if (compactBeforeCurrent.has(token) || compactAfterPrevious.has(previousToken)) {
      rendered = `${rendered}${token}`;
    } else {
      rendered = `${rendered} ${token}`;
    }
    previousToken = token;
  });
  return rendered.trim();
};

const buildDragDropFinal = (meta: DragDropMeta, slots: Array<DragDropSlotValue | null>): string => {
  if (meta.blankCount <= 0) return '';
  if (meta.hasBlanksInTarget) {
    return meta.segments.reduce((acc, segment, idx) => {
      const token = slots[idx]?.token || '';
      if (idx < meta.blankCount) {
        return `${acc}${segment}${token}`;
      }
      return `${acc}${segment}`;
    }, '');
  }
  const prefix = String(meta.target || '').trim();
  const placed = slots
    .slice(0, meta.blankCount)
    .map((slot) => slot?.token || '')
    .filter((token: string) => token.length > 0);
  if (meta.sourceType === 'drag_drop_code') {
    return joinCodeTokens(placed, prefix);
  }
  return [prefix, ...placed].filter(Boolean).join(' ').trim();
};

const normalizeStringList = (raw: any): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => matchingValueText(item).trim())
    .filter((item) => item.length > 0);
};

const formatStudentResponseText = (responseText?: string, responsePayload?: Record<string, any> | null): string => {
  const trimmedText = String(responseText || '').trim();
  if (trimmedText) return trimmedText;
  const payload = responsePayload && typeof responsePayload === 'object' && !Array.isArray(responsePayload)
    ? responsePayload
    : {};
  const directText = [payload.text, payload.answer, payload.value]
    .map((value) => {
      if (typeof value === 'string') return value.trim();
      if (value && typeof value === 'object') {
        if (typeof value.text === 'string') return value.text.trim();
        if (typeof value.label === 'string') return value.label.trim();
        if (typeof value.value === 'string') return value.value.trim();
      }
      return '';
    })
    .find((value) => value.length > 0);
  if (directText) return directText;
  if (Array.isArray(payload.selected_indices) && payload.selected_indices.length > 0) {
    return payload.selected_indices.map((value: any) => String(value)).join(', ');
  }
  if (Array.isArray(payload.ordering) && payload.ordering.length > 0) {
    return normalizeStringList(payload.ordering).join('\n');
  }
  if (Array.isArray(payload.ordered_items) && payload.ordered_items.length > 0) {
    return normalizeStringList(payload.ordered_items).join('\n');
  }
  if (payload.pairs && typeof payload.pairs === 'object') {
    const pairs = matchingPairsToTextLines(payload.pairs);
    if (pairs.length > 0) return pairs.join('\n');
  }
  return Object.keys(payload).length > 0 ? JSON.stringify(payload) : '';
};

const getAiMessageTitle = (message: AssignmentAiThreadMessage): string => {
  if (message.action_type === 'missed_answer_more_detail') return 'More detail';
  if (message.action_type === 'missed_answer_followup') return 'Question';
  return 'Step-by-step explanation';
};

const getAiMessageContent = (message: AssignmentAiThreadMessage): string => (
  String(message.content_md || message.explanation_md || '').trim()
);

const isAiChatReply = (message: AssignmentAiThreadMessage): boolean => (
  message.response_style === 'chat_reply' || message.action_type === 'missed_answer_followup'
);

const normalizeStudentAiMarkdown = (markdown: string): string => {
  let next = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!next) return '';
  next = next.replace(/```(?:[\w+-]+)?\n([^\n`]{1,48})\n```/g, (_match, code) => `\`${String(code || '').trim()}\``);
  next = next.replace(/([^\n])\n(`[^`\n]{1,48}`)\n([^\n])/g, '$1 $2 $3');
  return next.replace(/\n{3,}/g, '\n\n').trim();
};

const getAiLoadingLabel = (actionType?: string | null): string => {
  if (actionType === 'missed_answer_more_detail') return 'Adding more detail...';
  if (actionType === 'missed_answer_followup') return 'Answering your question...';
  return 'Generating explanation...';
};

const getMatchingConfig = (step: AssignmentDetailStep): { leftItems: string[]; rightItems: string[]; expectedPairs: Record<string, string> } => {
  const sourceItem = getSourceItem(step);
  const pairRows = normalizeMatchingDraftRows(sourceItem.pairs);
  const leftItems = normalizeStringList(sourceItem.left_items);
  const rightItems = normalizeStringList(sourceItem.right_items);

  const fallbackLeft = pairRows.map((row) => row.left.trim()).filter((item) => item.length > 0);
  const fallbackRight = Array.from(new Set(pairRows.map((row) => row.right.trim()).filter((item) => item.length > 0)));
  const expectedPairs = matchingRowsToPairs(pairRows);

  return {
    leftItems: leftItems.length > 0 ? leftItems : fallbackLeft,
    rightItems: rightItems.length > 0 ? rightItems : fallbackRight,
    expectedPairs,
  };
};

const isReplayStepHelpEnabled = (step?: { content?: Record<string, any> | null } | null): boolean => {
  const support = step?.content && typeof step.content === 'object' ? step.content.support : null;
  return !(support && typeof support === 'object' && support.enabled === false);
};

export const AssignmentDetail: React.FC = () => {
  const { user } = useAuth();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const redesignEnabled = isUiRedesignPageEnabled('v2_student_assignment_detail');
  const [detail, setDetail] = useState<AssignmentDetailResponse | null>(null);
  const [lessonPlanTitle, setLessonPlanTitle] = useState<string>('');
  const [sectionName, setSectionName] = useState<string>('');
  const [hookId, setHookId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState<Record<number, boolean>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [activeStepId, setActiveStepId] = useState<number | null>(null);
  const [singleChoice, setSingleChoice] = useState<Record<number, number | null>>({});
  const [multiChoice, setMultiChoice] = useState<Record<number, number[]>>({});
  const [guidedResponses, setGuidedResponses] = useState<Record<number, Record<string, string>>>({});
  const [guidedCardIndices, setGuidedCardIndices] = useState<Record<number, number>>({});
  const [reasoningTexts, setReasoningTexts] = useState<Record<number, string>>({});
  const [confidenceLevels, setConfidenceLevels] = useState<Record<number, number | null>>({});
  const [showOptionalReasoningByStep, setShowOptionalReasoningByStep] = useState<Record<number, boolean>>({});
  const [orderingValues, setOrderingValues] = useState<Record<number, string[]>>({});
  const [matchingValues, setMatchingValues] = useState<Record<number, Record<string, string>>>({});
  const [dragDropValues, setDragDropValues] = useState<Record<number, Array<DragDropSlotValue | null>>>({});
  const [previousAnswersData, setPreviousAnswersData] = useState<PreviousAnswersPayload | null>(null);
  const [previousAnswersLoading, setPreviousAnswersLoading] = useState(false);
  const [previousAnswersError, setPreviousAnswersError] = useState('');
  const [previousAnswersOpen, setPreviousAnswersOpen] = useState(false);
  const [aiThreadsByStep, setAiThreadsByStep] = useState<Record<number, AssignmentAiThreadPreview | null>>({});
  const [aiAssistLoadingByStep, setAiAssistLoadingByStep] = useState<Record<number, string | null>>({});
  const [aiAssistErrorByStep, setAiAssistErrorByStep] = useState<Record<number, string>>({});
  const [aiQuestionOpenByStep, setAiQuestionOpenByStep] = useState<Record<number, boolean>>({});
  const [aiQuestionDraftByStep, setAiQuestionDraftByStep] = useState<Record<number, string>>({});
  const [activeReviewStepId, setActiveReviewStepId] = useState<number | null>(null);
  const [stepFilter, setStepFilter] = useState<'all' | 'missed'>('all');
  const [replaySaving, setReplaySaving] = useState(false);
  const [replayError, setReplayError] = useState('');
  const [examSubmitting, setExamSubmitting] = useState(false);
  const [retakeSubmitting, setRetakeSubmitting] = useState(false);
  const [retakeRemediationResponse, setRetakeRemediationResponse] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [assessmentExporting, setAssessmentExporting] = useState(false);
  const [externalSubmissionConfirming, setExternalSubmissionConfirming] = useState(false);
  const [assessmentDocumentNotice, setAssessmentDocumentNotice] = useState('');
  const dragDropInstanceIdRef = useRef(1);

  const normalize = useMemo(() => (value?: string | null) => (value || '').trim().replace(/\s+/g, ' '), []);
  const replayMode = detail?.delivery_mode === 'replay_exam' || detail?.delivery_mode === 'replay_lesson';
  const fallbackStatus = detail
    ? (
      detail.steps.every((step) => step.has_current_evidence)
        ? 'completed'
        : detail.steps.some((step) => step.has_current_evidence)
          ? 'in_progress'
          : 'not_started'
    )
    : 'not_started';
  const studentStatus = detail?.student_status || fallbackStatus;
  const isAssessment = detail?.purpose === 'assessment';
  const isPractice = detail?.purpose === 'practice';
  const allowAiGuidance = detail?.assessment_settings?.allow_ai_guidance !== false;
  const showObjectiveResults = detail?.assessment_settings?.show_objective_results !== false;
  const review = detail?.review ?? null;
  const statusStyles = useMemo(() => ({
    not_started: 'bg-slate-100 text-slate-700',
    in_progress: 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
    excused: 'bg-purple-100 text-purple-700',
  }), []);
  const shellActions = (
    <Link
      to="/v2/student"
      className={redesignEnabled
        ? 'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-surface-alt hover:text-text'
        : 'text-sm text-indigo-600 hover:text-indigo-800'}
    >
      Back to dashboard
    </Link>
  );
  const loadDetail = async () => {
    if (!assignmentId) return;
    try {
      const response = await assignmentsApi.detail(Number(assignmentId));
      setDetail(response);
    } catch (error: any) {
      if (error?.response?.status === 428) {
        navigate('/v2/student', {
          replace: true,
          state: { gate: error?.response?.data?.gate || null },
        });
        return;
      }
      setReplayError(error?.response?.data?.error || 'Could not load assignment.');
    }
  };

  const resetReplayAttemptState = () => {
    setDrafts({});
    setSubmitting({});
    setErrors({});
    setActiveStepId(null);
    setSingleChoice({});
    setMultiChoice({});
    setGuidedResponses({});
    setGuidedCardIndices({});
    setReasoningTexts({});
    setConfidenceLevels({});
    setShowOptionalReasoningByStep({});
    setOrderingValues({});
    setMatchingValues({});
    setDragDropValues({});
    setPreviousAnswersData(null);
    setPreviousAnswersLoading(false);
    setPreviousAnswersError('');
    setPreviousAnswersOpen(false);
    setAiThreadsByStep({});
    setAiAssistLoadingByStep({});
    setAiAssistErrorByStep({});
    setAiQuestionOpenByStep({});
    setAiQuestionDraftByStep({});
    setActiveReviewStepId(null);
    setStepFilter('all');
    setReplayError('');
  };

  useEffect(() => {
    setDetail(null);
    resetReplayAttemptState();
    loadDetail();
  }, [assignmentId]);

  useEffect(() => {
    const loadMeta = async () => {
      if (!detail) return;
      if (detail.lesson_plan_title && detail.section_name) {
        setLessonPlanTitle(detail.lesson_plan_title); setSectionName(detail.section_name); return;
      }
      const [lessonPlanRes, sectionsRes] = await Promise.all([
        lessonPlansApi.get(detail.lesson_plan),
        sectionsApi.list(),
      ]);
      setLessonPlanTitle(lessonPlanRes.data?.title || `Lesson Plan #${detail.lesson_plan}`);
      setHookId(lessonPlanRes.data?.hook ?? null);
      const sections = Array.isArray(sectionsRes.data) ? sectionsRes.data : sectionsRes.data?.results || [];
      const section = sections.find((item: any) => item.id === detail.section);
      setSectionName(section?.name || `Section #${detail.section}`);
    };
    loadMeta();
  }, [detail]);

  useEffect(() => {
    if (!detail) return;
    setDrafts((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const latestEvidenceText = step.latest_evidence?.content?.text || '';
        const latestResponse = step.latest_response?.response || {};
        const latestText = latestEvidenceText || String(latestResponse.text || latestResponse.answer || latestResponse.value || '');
        if (next[step.step_id] === undefined || next[step.step_id] === '') {
          next[step.step_id] = latestText;
        }
      });
      return next;
    });

    setSingleChoice((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const existingValue = next[step.step_id];
        if (Number.isInteger(existingValue) && Number(existingValue) >= 0) return;
        const options = getOptions(step);
        if (options.length === 0) return;
        const latestResponse = step.latest_response?.response || {};
        const rawSingle = latestResponse.selected_index ?? latestResponse.value;
        const parsedIndex = Number(rawSingle);
        if (Number.isInteger(parsedIndex)) {
          next[step.step_id] = parsedIndex;
          return;
        }
        if (typeof rawSingle === 'string') {
          const optionIndex = options.findIndex((item) => item === rawSingle);
          if (optionIndex >= 0) {
            next[step.step_id] = optionIndex;
          }
        }
      });
      return next;
    });

    setMultiChoice((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const existingValues = next[step.step_id] || [];
        if (existingValues.length > 0) return;
        const latestResponse = step.latest_response?.response || {};
        const raw = latestResponse.selected_indices ?? latestResponse.value;
        const values = Array.isArray(raw)
          ? raw
          : (typeof raw === 'string' ? raw.split(',') : []);
        if (!Array.isArray(values)) return;
        const parsed = values
          .map((item: any) => Number(item))
          .filter((item: number) => Number.isInteger(item));
        if (parsed.length > 0) {
          next[step.step_id] = Array.from(new Set(parsed)).sort((a, b) => a - b);
        }
      });
      return next;
    });

    setGuidedResponses((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const latestResponse = step.latest_response?.response || {};
        const cards = getGuidedSequenceCards(step);
        if (cards.length === 0) return;
        const values = cards.reduce((acc: Record<string, string>, card) => {
          const raw = latestResponse[card.key];
          if (typeof raw === 'string') {
            acc[card.key] = raw;
          } else if (raw !== undefined && raw !== null) {
            acc[card.key] = String(raw);
          }
          return acc;
        }, {});
        if (Object.keys(values).length > 0) {
          next[step.step_id] = values;
        }
      });
      return next;
    });

    setReasoningTexts((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const latestResponse = step.latest_response?.response || {};
        const reasoning = latestResponse.reasoning_text ?? latestResponse.reasoning;
        if (typeof reasoning === 'string' && reasoning.trim()) {
          next[step.step_id] = reasoning;
        }
      });
      return next;
    });

    setConfidenceLevels((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const latestResponse = step.latest_response?.response || {};
        const raw = latestResponse.confidence_level ?? latestResponse.confidence;
        const parsed = Number(raw);
        if (Number.isInteger(parsed) && parsed >= 1 && parsed <= 5) {
          next[step.step_id] = parsed;
        }
      });
      return next;
    });

    setShowOptionalReasoningByStep((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        const latestResponse = step.latest_response?.response || {};
        const reasoning = String(latestResponse.reasoning_text ?? latestResponse.reasoning ?? '').trim();
        if (reasoning) {
          next[step.step_id] = true;
        }
      });
      return next;
    });

    setOrderingValues(() => {
      const next: Record<number, string[]> = {};
      detail.steps.forEach((step) => {
        if (getSourceType(step) !== 'ordering') return;
        const latestResponse = step.latest_response?.response || {};
        const sourceItem = getSourceItem(step);
        const rawOrdering = latestResponse.ordering ?? latestResponse.ordered_items ?? latestResponse.value ?? latestResponse.answer;
        const fromResponse = normalizeStringList(rawOrdering);
        const fromItems = normalizeStringList(sourceItem.items);
        next[step.step_id] = fromResponse.length > 0 ? fromResponse : fromItems;
      });
      return next;
    });

    setMatchingValues(() => {
      const next: Record<number, Record<string, string>> = {};
      detail.steps.forEach((step) => {
        if (getSourceType(step) !== 'matching') return;
        const latestResponse = step.latest_response?.response || {};
        const rawPairs = latestResponse.pairs && typeof latestResponse.pairs === 'object'
          ? latestResponse.pairs
          : (latestResponse.value && typeof latestResponse.value === 'object' ? latestResponse.value : {});
        const pairMap = matchingRowsToPairs(normalizeMatchingDraftRows(rawPairs));
        next[step.step_id] = pairMap;
      });
      return next;
    });

    setDragDropValues(() => {
      const next: Record<number, Array<DragDropSlotValue | null>> = {};
      detail.steps.forEach((step) => {
        const sourceType = getSourceType(step);
        if (!['drag_drop_code', 'drag_drop_fill_blanks', 'drag_drop_keywords'].includes(sourceType)) return;
        const meta = getDragDropMeta(step);
        if (meta.blankCount <= 0) {
          next[step.step_id] = [];
          return;
        }
        const latestResponse = step.latest_response?.response || {};
        const rawPlaced = Array.isArray(latestResponse.placed_tokens)
          ? latestResponse.placed_tokens
          : (Array.isArray(latestResponse.answer_tokens)
            ? latestResponse.answer_tokens
            : (Array.isArray(latestResponse.value) ? latestResponse.value : []));
        const slots = Array.from({ length: meta.blankCount }, (_, idx) => {
          const token = String(rawPlaced[idx] ?? '').trim();
          if (!token) return null;
          return {
            instanceId: dragDropInstanceIdRef.current++,
            token,
          };
        });
        next[step.step_id] = slots;
      });
      return next;
    });

    setAiThreadsByStep((prev) => {
      const next = { ...prev };
      detail.steps.forEach((step) => {
        if (step.ai_thread_preview && typeof step.ai_thread_preview === 'object') {
          next[step.step_id] = step.ai_thread_preview;
        } else if (!(step.step_id in next)) {
          next[step.step_id] = null;
        }
      });
      const reviewMissedSteps = detail.review && Array.isArray(detail.review.missed_steps)
        ? detail.review.missed_steps
        : [];
      reviewMissedSteps.forEach((item) => {
        if (item.ai_thread_preview && typeof item.ai_thread_preview === 'object') {
          next[item.step_id] = item.ai_thread_preview;
        } else if (!(item.step_id in next)) {
          next[item.step_id] = null;
        }
      });
      return next;
    });

    if (replayMode) {
      const availableStepIds = detail.steps.map((step) => step.step_id);
      const serverCurrentStepId = detail.progress?.current_step_id;
      setActiveStepId((prev) => {
        if (prev && availableStepIds.includes(prev)) return prev;
        if (serverCurrentStepId && availableStepIds.includes(serverCurrentStepId)) return serverCurrentStepId;
        return availableStepIds[0] ?? null;
      });
    }
  }, [detail, replayMode]);

  const currentStep = useMemo(() => {
    if (!detail || !replayMode) return null;
    if (activeStepId) {
      return detail.steps.find((step) => step.step_id === activeStepId) || null;
    }
    return detail.steps[0] || null;
  }, [detail, replayMode, activeStepId]);

  const restoreAssignmentDraft = useCallback((saved: Record<string, any>) => {
    if (!saved || !Object.keys(saved).length) return;
    if (saved.drafts) setDrafts(saved.drafts);
    if (saved.singleChoice) setSingleChoice(saved.singleChoice);
    if (saved.multiChoice) setMultiChoice(saved.multiChoice);
    if (saved.guidedResponses) setGuidedResponses(saved.guidedResponses);
    if (saved.guidedCardIndices) setGuidedCardIndices(saved.guidedCardIndices);
    if (saved.reasoningTexts) setReasoningTexts(saved.reasoningTexts);
    if (saved.confidenceLevels) setConfidenceLevels(saved.confidenceLevels);
    if (saved.orderingValues) setOrderingValues(saved.orderingValues);
    if (saved.matchingValues) setMatchingValues(saved.matchingValues);
    if (saved.dragDropValues) setDragDropValues(saved.dragDropValues);
    if (detail?.steps.some(item => item.step_id === saved.activeStepId)) setActiveStepId(saved.activeStepId);
  }, [detail]);
  const recoveryPayload = useMemo(() => ({ activeStepId, drafts, singleChoice, multiChoice,
    guidedResponses, guidedCardIndices, reasoningTexts, confidenceLevels, orderingValues,
    matchingValues, dragDropValues }), [activeStepId, drafts, singleChoice, multiChoice,
    guidedResponses, guidedCardIndices, reasoningTexts, confidenceLevels, orderingValues,
    matchingValues, dragDropValues]);
  const draftRecovery = useAssignmentDraft(user?.id, detail?.id, detail?.attempt_number || 1,
    Boolean(detail && detail.id === Number(assignmentId) && detail.delivery_mode === 'replay_lesson' && !isAssessment && activeStepId),
    recoveryPayload, restoreAssignmentDraft, studentStatus === 'completed');

  const reviewMissedSteps = useMemo(() => {
    const missedSteps = review?.missed_steps;
    return Array.isArray(missedSteps) ? missedSteps : [];
  }, [review]);
  const showCompletionReview = Boolean(
    replayMode && showObjectiveResults && review?.available && studentStatus === 'completed'
  );
  const reviewStepIdsKey = useMemo(() => reviewMissedSteps.map((item) => item.step_id).join(','), [reviewMissedSteps]);
  const activeReviewItem = useMemo(() => (
    reviewMissedSteps.find((item) => item.step_id === activeReviewStepId) || null
  ), [reviewMissedSteps, activeReviewStepId]);
  const activeReviewIndex = useMemo(() => (
    activeReviewItem ? reviewMissedSteps.findIndex((item) => item.step_id === activeReviewItem.step_id) : -1
  ), [reviewMissedSteps, activeReviewItem]);

  useEffect(() => {
    if (!showCompletionReview || reviewMissedSteps.length === 0) {
      setActiveReviewStepId(null);
      return;
    }
    const missedStepIds = new Set(reviewMissedSteps.map((item) => item.step_id));
    setActiveReviewStepId((prev) => {
      if (currentStep && missedStepIds.has(currentStep.step_id)) return currentStep.step_id;
      if (prev && missedStepIds.has(prev)) return prev;
      return reviewMissedSteps[0]?.step_id ?? null;
    });
  }, [showCompletionReview, reviewStepIdsKey, currentStep]);

  const isExamMode = detail?.delivery_mode === 'replay_exam';
  const currentAttemptNumber = detail?.attempt_number || 1;
  const canRetakeReplay = Boolean(replayMode && detail?.can_retake);
  const canShowReplayScoring = Boolean(detail) && showObjectiveResults && (!isExamMode || studentStatus === 'completed');
  const canShowMissedNavigation = Boolean(detail) && showObjectiveResults && (!isExamMode || studentStatus === 'completed');
  const retakeButtonLabel = isExamMode ? 'Retake Quiz' : 'Try Again';
  const retakeLoadingLabel = isExamMode ? 'Starting retake…' : 'Starting new attempt…';
  const shouldShowReplayScoreSummary = canShowReplayScoring
    && detail?.progress?.score_percent !== null
    && (detail?.progress?.scorable_count || 0) > 0;
  const shouldShowReplayScoredCount = canShowReplayScoring && (detail?.progress?.scorable_count || 0) > 0;
  const stepScoreStateById = useMemo(() => {
    if (!detail) return {};
    return detail.steps.reduce<Record<number, StepScoreState>>((acc, step) => {
      if (!stepAcceptsReplayResponse(step)) {
        acc[step.step_id] = 'hidden';
        return acc;
      }
      if (!step.has_response) {
        acc[step.step_id] = 'unanswered';
        return acc;
      }
      if (!canShowReplayScoring) {
        acc[step.step_id] = 'saved_unscored';
        return acc;
      }
      if (step.latest_response?.is_correct === true) {
        acc[step.step_id] = 'correct';
        return acc;
      }
      if (step.latest_response?.is_correct === false) {
        acc[step.step_id] = 'incorrect';
        return acc;
      }
      acc[step.step_id] = 'saved_unscored';
      return acc;
    }, {});
  }, [detail, canShowReplayScoring]);
  const missedStepIds = useMemo(() => {
    if (!detail || !canShowMissedNavigation) return new Set<number>();
    if (isExamMode) {
      return new Set(reviewMissedSteps.map((item) => item.step_id));
    }
    return new Set(
      detail.steps
        .filter((step) => step.latest_response?.is_correct === false)
        .map((step) => step.step_id),
    );
  }, [detail, canShowMissedNavigation, isExamMode, reviewMissedSteps]);
  const visibleReplaySteps = useMemo(() => {
    if (!detail) return [];
    if (stepFilter !== 'missed') return detail.steps;
    return detail.steps.filter((step) => missedStepIds.has(step.step_id));
  }, [detail, stepFilter, missedStepIds]);
  const currentStepIndex = useMemo(() => {
    if (!currentStep) return -1;
    return visibleReplaySteps.findIndex((step) => step.step_id === currentStep.step_id);
  }, [currentStep, visibleReplaySteps]);
  const showMissedFilterControls = canShowMissedNavigation;
  const showMissedEmptyState = stepFilter === 'missed' && visibleReplaySteps.length === 0;

  useEffect(() => {
    if (!showMissedFilterControls && stepFilter !== 'all') {
      setStepFilter('all');
    }
  }, [showMissedFilterControls, stepFilter]);

  useEffect(() => {
    if (stepFilter !== 'missed' || visibleReplaySteps.length === 0) return;
    if (currentStep && visibleReplaySteps.some((step) => step.step_id === currentStep.step_id)) return;
    setActiveStepId(visibleReplaySteps[0].step_id);
  }, [stepFilter, visibleReplaySteps, currentStep]);

  const aiMarkdownComponents = useMemo(() => ({
    p({ children }: any) {
      return <p className="my-2 leading-relaxed text-slate-800">{children}</p>;
    },
    h2({ children }: any) {
      return <h2 className="mt-4 text-base font-semibold text-slate-900">{children}</h2>;
    },
    h3({ children }: any) {
      return <h3 className="mt-3 text-sm font-semibold text-slate-900">{children}</h3>;
    },
    ul({ children }: any) {
      return <ul className="my-2 list-disc pl-5 space-y-1 text-slate-800">{children}</ul>;
    },
    ol({ children }: any) {
      return <ol className="my-2 list-decimal pl-5 space-y-1 text-slate-800">{children}</ol>;
    },
    li({ children }: any) {
      return <li className="leading-relaxed">{children}</li>;
    },
    strong({ children }: any) {
      return <strong className="font-semibold text-slate-900">{children}</strong>;
    },
    em({ children }: any) {
      return <em className="italic">{children}</em>;
    },
    blockquote({ children }: any) {
      return <blockquote className="my-3 border-l-4 border-slate-300 pl-3 text-slate-700">{children}</blockquote>;
    },
    a({ href, children }: any) {
      return (
        <a href={href} target="_blank" rel="noreferrer" className="text-indigo-700 underline">
          {children}
        </a>
      );
    },
    pre({ children }: any) {
      return <>{children}</>;
    },
    code({ inline, className, children, ...props }: any) {
      const raw = String(children || '').replace(/\n$/, '');
      const compactBlock = !inline && !raw.includes('\n') && raw.length <= 48;
      if (inline || compactBlock) {
        return (
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.92em] text-slate-900" {...props}>
            {raw}
          </code>
        );
      }
      return (
        <pre className="mt-3 overflow-x-auto rounded-xl bg-slate-900 p-3 text-sm text-slate-100">
          <code className={className} {...props}>{raw}</code>
        </pre>
      );
    },
  }), []);

  const getAiThreadForStep = useCallback((stepId: number, fallbackThread?: AssignmentAiThreadPreview | null) => {
    const thread = aiThreadsByStep[stepId];
    if (thread && typeof thread === 'object') return thread;
    if (fallbackThread && typeof fallbackThread === 'object') return fallbackThread;
    return null;
  }, [aiThreadsByStep]);

  const requestAiAssist = useCallback(async (
    stepId: number,
    actionType: 'missed_answer_explanation' | 'missed_answer_more_detail' | 'missed_answer_followup',
    options?: {
      question?: string;
      parentEventId?: number | null;
    },
  ) => {
    if (!assignmentId) return;
    const question = String(options?.question || '').trim();
    const parentEventId = typeof options?.parentEventId === 'number' ? options.parentEventId : null;
    setAiAssistLoadingByStep((prev) => ({ ...prev, [stepId]: actionType }));
    setAiAssistErrorByStep((prev) => ({ ...prev, [stepId]: '' }));
    try {
      const payload: {
        step_id: number;
        action_type: 'missed_answer_explanation' | 'missed_answer_more_detail' | 'missed_answer_followup';
        question?: string;
        parent_event_id?: number;
      } = {
        step_id: stepId,
        action_type: actionType,
      };
      if (question) payload.question = question;
      if (parentEventId !== null) payload.parent_event_id = parentEventId;
      const response = await assignmentsApi.aiAssist(Number(assignmentId), payload);
      const nextThread = response?.thread && typeof response.thread === 'object'
        ? response.thread as AssignmentAiThreadPreview
        : null;
      setAiThreadsByStep((prev) => ({ ...prev, [stepId]: nextThread }));
      if (actionType === 'missed_answer_followup') {
        setAiQuestionDraftByStep((prev) => ({ ...prev, [stepId]: '' }));
        setAiQuestionOpenByStep((prev) => ({ ...prev, [stepId]: false }));
      }
    } catch (error: any) {
      setAiAssistErrorByStep((prev) => ({
        ...prev,
        [stepId]: error?.response?.data?.error || 'Could not load an explanation right now.',
      }));
    } finally {
      setAiAssistLoadingByStep((prev) => ({ ...prev, [stepId]: null }));
    }
  }, [assignmentId]);

  const renderAiPanel = useCallback((
    stepId: number,
    explanationSupported?: boolean,
    fallbackThread?: AssignmentAiThreadPreview | null,
  ) => {
    if (!explanationSupported || !allowAiGuidance) return null;
    const thread = getAiThreadForStep(stepId, fallbackThread);
    const messages = thread && Array.isArray(thread.messages) ? thread.messages : [];
    const hasThread = messages.length > 0;
    const loadingAction = aiAssistLoadingByStep[stepId];
    const isLoading = Boolean(loadingAction);
    const errorMessage = aiAssistErrorByStep[stepId];
    const latestEventId = messages[messages.length - 1]?.id ?? thread?.root_event_id ?? null;
    const questionOpen = Boolean(aiQuestionOpenByStep[stepId]);
    const questionDraft = aiQuestionDraftByStep[stepId] || '';

    return (
      <section className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">Missed-answer help</p>
          {!hasThread && (
            <button
              type="button"
              className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
              onClick={() => {
                void requestAiAssist(stepId, 'missed_answer_explanation');
              }}
              disabled={isLoading}
            >
              {isLoading ? getAiLoadingLabel(loadingAction) : 'Explain step-by-step'}
            </button>
          )}
        </div>
        {errorMessage && (
          <p className="text-sm text-rose-700">{errorMessage}</p>
        )}
        {hasThread && (
          <div className="space-y-3">
            {messages.map((message) => {
              const messageContent = getAiMessageContent(message);
              if (isAiChatReply(message)) {
                return (
                  <div key={`ai-thread-${stepId}-${message.id}`} className="space-y-2">
                    {message.request_text && (
                      <div className="ml-auto max-w-2xl rounded-2xl rounded-br-md bg-indigo-600 px-4 py-3 text-sm text-white shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-100">Your question</p>
                        <p className="mt-1 whitespace-pre-wrap leading-relaxed">{message.request_text}</p>
                      </div>
                    )}
                    <div className="max-w-2xl rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tutor reply</p>
                        {message.created_at && (
                          <span className="text-xs text-slate-500">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-sm">
                        <ReactMarkdown components={aiMarkdownComponents}>
                          {normalizeStudentAiMarkdown(messageContent)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={`ai-thread-${stepId}-${message.id}`} className="rounded border border-indigo-100 bg-white px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{getAiMessageTitle(message)}</p>
                    {message.created_at && (
                      <span className="text-xs text-slate-500">
                        {new Date(message.created_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-sm">
                    <ExplainBroadcastContent markdown={normalizeStudentAiMarkdown(messageContent)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {isLoading && hasThread && (
          <p className="text-sm text-slate-600">{getAiLoadingLabel(loadingAction)}</p>
        )}
        {hasThread && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
              onClick={() => {
                if (!latestEventId) return;
                void requestAiAssist(stepId, 'missed_answer_more_detail', { parentEventId: latestEventId });
              }}
              disabled={isLoading || !latestEventId}
            >
              More detail
            </button>
            <button
              type="button"
              className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
              onClick={() => setAiQuestionOpenByStep((prev) => ({ ...prev, [stepId]: !questionOpen }))}
              disabled={isLoading}
            >
              {questionOpen ? 'Cancel question' : 'Ask a question'}
            </button>
          </div>
        )}
        {hasThread && questionOpen && (
          <div className="space-y-2">
            <textarea
              className="w-full rounded-md border border-slate-200 p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
              value={questionDraft}
              onChange={(event) => setAiQuestionDraftByStep((prev) => ({ ...prev, [stepId]: event.target.value }))}
              placeholder="Ask about this exact problem or one concept used here."
              disabled={isLoading}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => setAiQuestionOpenByStep((prev) => ({ ...prev, [stepId]: false }))}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
                onClick={() => {
                  if (!latestEventId || !questionDraft.trim()) return;
                  void requestAiAssist(stepId, 'missed_answer_followup', {
                    question: questionDraft,
                    parentEventId: latestEventId,
                  });
                }}
                disabled={isLoading || !latestEventId || !questionDraft.trim()}
              >
                Send question
              </button>
            </div>
          </div>
        )}
      </section>
    );
  }, [
    allowAiGuidance,
    aiAssistErrorByStep,
    aiAssistLoadingByStep,
    aiMarkdownComponents,
    aiQuestionDraftByStep,
    aiQuestionOpenByStep,
    getAiThreadForStep,
    requestAiAssist,
  ]);

  const getGuidedCardError = useCallback((card: GuidedSequenceCard, rawValue: string) => {
    const value = String(rawValue || '').trim();
    const prompt = card.prompt || card.key;
    const validation = card.validation && typeof card.validation === 'object'
      ? card.validation
      : {};

    if (card.required && !value) return `${prompt} is required.`;
    if (!value) return '';

    const minWordsRaw = Number(validation.min_words);
    const minWords = Number.isInteger(minWordsRaw) && minWordsRaw > 0 ? minWordsRaw : 0;
    if (minWords > 0) {
      const wordCount = value.split(/\s+/).filter(Boolean).length;
      if (wordCount < minWords) return `${prompt} must be at least ${minWords} words.`;
    }

    const minCharsRaw = Number(validation.min_chars);
    const minChars = Number.isInteger(minCharsRaw) && minCharsRaw > 0 ? minCharsRaw : 0;
    if (minChars > 0 && value.length < minChars) {
      return `${prompt} must be at least ${minChars} characters.`;
    }

    const maxCharsRaw = Number(validation.max_chars);
    const maxChars = Number.isInteger(maxCharsRaw) && maxCharsRaw > 0 ? maxCharsRaw : 0;
    if (maxChars > 0 && value.length > maxChars) {
      return `${prompt} must be at most ${maxChars} characters.`;
    }

    const mustStartWith = typeof validation.must_start_with === 'string'
      ? validation.must_start_with.trim()
      : '';
    if (mustStartWith && !value.toLowerCase().startsWith(mustStartWith.toLowerCase())) {
      return `${prompt} must start with "${mustStartWith}".`;
    }

    return '';
  }, []);

  useEffect(() => {
    if (!currentStep) {
      setPreviousAnswersData(null);
      setPreviousAnswersLoading(false);
      setPreviousAnswersError('');
      setPreviousAnswersOpen(false);
      return;
    }
    const signalConfig = currentStep.signal_config || {};
    const previousAnswersConfig = signalConfig.previous_answers ?? signalConfig.previousAnswers;
    if (!assignmentId || !previousAnswersConfig || typeof previousAnswersConfig !== 'object' || Array.isArray(previousAnswersConfig)) {
      setPreviousAnswersData(null);
      setPreviousAnswersLoading(false);
      setPreviousAnswersError('');
      setPreviousAnswersOpen(false);
      return;
    }

    let isActive = true;
    setPreviousAnswersLoading(true);
    setPreviousAnswersError('');
    assignmentsApi.previousAnswers(Number(assignmentId), currentStep.step_id)
      .then((payload) => {
        if (!isActive) return;
        const normalized: PreviousAnswersPayload = {
          mode: typeof payload?.mode === 'string' ? payload.mode : 'optional',
          title: typeof payload?.title === 'string' ? payload.title : '',
          display: typeof payload?.display === 'string' ? payload.display : '',
          allow_insert: Boolean(payload?.allow_insert),
          items: Array.isArray(payload?.items)
            ? payload.items
              .map((item: any) => {
                const stepId = Number(item?.step_id);
                if (!Number.isInteger(stepId) || stepId <= 0) return null;
                const responseText = String(item?.response_text || '').trim();
                if (!responseText) return null;
                return {
                  step_id: stepId,
                  label: String(item?.label || item?.step_title || `Step ${stepId}`).trim(),
                  step_title: String(item?.step_title || '').trim(),
                  response_text: responseText,
                  submitted_at: typeof item?.submitted_at === 'string' ? item.submitted_at : null,
                };
              })
              .filter((item: PreviousAnswersItem | null): item is PreviousAnswersItem => Boolean(item))
            : [],
        };
        setPreviousAnswersData(normalized);
        setPreviousAnswersOpen(String(normalized.mode || '').trim().toLowerCase() === 'on');
      })
      .catch((error: any) => {
        if (!isActive) return;
        setPreviousAnswersData(null);
        setPreviousAnswersOpen(false);
        setPreviousAnswersError(error?.response?.data?.error || 'Could not load previous answers.');
      })
      .finally(() => {
        if (isActive) {
          setPreviousAnswersLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [assignmentId, currentStep]);

  const handleSubmitEvidence = async (step: AssignmentDetailStep) => {
    if (!hookId) return;
    const draft = drafts[step.step_id] || '';
    const latestText = step.latest_evidence?.content?.text || '';
    if (normalize(draft) === normalize(latestText)) return;
    setSubmitting((prev) => ({ ...prev, [step.step_id]: true }));
    setErrors((prev) => ({ ...prev, [step.step_id]: '' }));
    try {
      const stage = step.stage_tag || 'define';
      const payload = {
        hook: hookId,
        stage,
        lesson_step: step.step_id,
        evidence_type: 'text',
        content: { text: draft },
      };
      await evidenceApi.submit(payload);
      await loadDetail();
    } catch (error) {
      setErrors((prev) => ({ ...prev, [step.step_id]: 'Could not submit evidence. Try again.' }));
    } finally {
      setSubmitting((prev) => ({ ...prev, [step.step_id]: false }));
    }
  };

  const toggleMultiChoice = (stepId: number, optionIndex: number) => {
    setMultiChoice((prev) => {
      const current = prev[stepId] || [];
      if (current.includes(optionIndex)) {
        return { ...prev, [stepId]: current.filter((item) => item !== optionIndex) };
      }
      return { ...prev, [stepId]: [...current, optionIndex] };
    });
  };

  const placeDragDropToken = useCallback((stepId: number, targetIndex: number, item: DragTokenItem, fromSlotIndex: number | null) => {
    setDragDropValues((prev) => {
      const current = Array.isArray(prev[stepId]) ? [...prev[stepId]] : [];
      const cleanToken = String(item?.token ?? '').trim();
      if (!cleanToken) return prev;
      const isSlotMove = item?.source === 'slot';
      const sourceSlotIndex = (
        isSlotMove
        && (
          (typeof item?.fromSlotIndex === 'number' && item.fromSlotIndex >= 0)
          || (typeof fromSlotIndex === 'number' && fromSlotIndex >= 0)
        )
      )
        ? (typeof item?.fromSlotIndex === 'number' && item.fromSlotIndex >= 0 ? item.fromSlotIndex : fromSlotIndex)
        : null;
      const nextInstanceId = isSlotMove
        ? (typeof item?.instanceId === 'number' && item.instanceId > 0
          ? item.instanceId
          : dragDropInstanceIdRef.current++)
        : dragDropInstanceIdRef.current++;
      const targetPrev = current[targetIndex] ?? null;
      for (let idx = 0; idx < current.length; idx += 1) {
        if (current[idx]?.instanceId === nextInstanceId) {
          current[idx] = null;
        }
      }
      if (sourceSlotIndex !== null && sourceSlotIndex < current.length) {
        current[sourceSlotIndex] = targetPrev;
      }
      current[targetIndex] = { instanceId: nextInstanceId, token: cleanToken };
      return { ...prev, [stepId]: current };
    });
  }, []);

  const handleDropDragDropItem = useCallback((stepId: number, targetIndex: number, item: DragTokenItem) => {
    const fromSlotIndexRaw = item?.fromSlotIndex;
    const fromSlotIndex = typeof fromSlotIndexRaw === 'number' && Number.isInteger(fromSlotIndexRaw)
      ? fromSlotIndexRaw
      : null;
    placeDragDropToken(stepId, targetIndex, item, fromSlotIndex);
  }, [placeDragDropToken]);

  const buildReplayPayload = (step: AssignmentDetailStep) => {
    const signalType = normalizeSignalType(step);
    const signalConfig = step.signal_config || {};
    const options = getOptions(step);
    const selectRaw = Number(signalConfig.select || 0);
    const isMulti = signalType === 'mcq_multi' || (Number.isFinite(selectRaw) && selectRaw > 1);
    const sourceType = getSourceType(step);
    const guidedCards = getGuidedSequenceCards(step);
    const reasoningText = String(reasoningTexts[step.step_id] || '').trim();
    const confidenceLevel = confidenceLevels[step.step_id];
    let payload: Record<string, any>;

    if (guidedCards.length > 0) {
      payload = guidedCards.reduce((acc, card) => {
        acc[card.key] = String(guidedResponses[step.step_id]?.[card.key] || '').trim();
        return acc;
      }, {} as Record<string, any>);
    } else if (options.length > 0) {
      if (isMulti) {
        payload = { selected_indices: multiChoice[step.step_id] || [] };
      } else {
        payload = { value: singleChoice[step.step_id] };
      }
    } else {
      const draft = drafts[step.step_id] || '';
      if (sourceType === 'ordering') {
        const fromOrderingState = orderingValues[step.step_id] || [];
        const orderedItems = fromOrderingState.length > 0
          ? fromOrderingState
          : draft
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
        payload = {
          type: 'ordering',
          ordered_items: orderedItems,
          ordering: orderedItems,
          value: orderedItems,
        };
      } else if (sourceType === 'matching') {
        const statePairs = matchingValues[step.step_id] || {};
        const hasStatePairs = Object.keys(statePairs).length > 0;
        const pairs: Record<string, string> = hasStatePairs
          ? Object.entries(statePairs).reduce((acc: Record<string, string>, [left, right]) => {
            const cleanLeft = String(left ?? '').trim();
            const cleanRight = String(right ?? '').trim();
            if (cleanLeft && cleanRight) {
              acc[cleanLeft] = cleanRight;
            }
            return acc;
          }, {})
          : {};
        if (!hasStatePairs) {
          draft.split('\n').forEach((line) => {
            const [left, right] = line.split('=>');
            if (left && right) {
              pairs[left.trim()] = right.trim();
            }
          });
        }
        payload = {
          type: 'matching',
          pairs,
          value: pairs,
        };
      } else if (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks' || sourceType === 'drag_drop_keywords') {
        const meta = getDragDropMeta(step);
        const slots = Array.isArray(dragDropValues[step.step_id]) ? dragDropValues[step.step_id] : [];
        const placedTokens = slots
          .slice(0, meta.blankCount)
          .map((slot) => slot?.token ?? null);
        const fallbackTokens = draft
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
        const tokenPayload = placedTokens.some((item) => String(item ?? '').trim().length > 0)
          ? placedTokens
          : fallbackTokens;
        payload = {
          type: sourceType,
          placed_tokens: tokenPayload,
          answer_tokens: tokenPayload,
          final: buildDragDropFinal(meta, slots),
          value: tokenPayload,
        };
      } else {
        payload = { text: draft, value: draft };
      }
    }

    if (typeof confidenceLevel === 'number' && Number.isInteger(confidenceLevel) && confidenceLevel >= 1 && confidenceLevel <= 5) {
      payload.confidence_level = confidenceLevel;
    }
    if (reasoningText) {
      payload.reasoning_text = reasoningText;
    }
    return payload;
  };

  const hasResponseForStep = useCallback((step: AssignmentDetailStep): boolean => {
    const options = getOptions(step);
    const signalType = normalizeSignalType(step);
    const selectRaw = Number(step.signal_config?.select || 0);
    const isMulti = signalType === 'mcq_multi' || (Number.isFinite(selectRaw) && selectRaw > 1);
    const requiredSelections = Number.isFinite(selectRaw) && selectRaw > 0 ? selectRaw : (isMulti ? 2 : 1);
    const guidedCards = getGuidedSequenceCards(step);
    if (guidedCards.length > 0) {
      return guidedCards.every((card) => {
        const value = guidedResponses[step.step_id]?.[card.key] || '';
        return !getGuidedCardError(card, value);
      });
    }
    if (options.length > 0) {
      if (isMulti) return (multiChoice[step.step_id] || []).length === requiredSelections;
      const selected = singleChoice[step.step_id];
      return Number.isInteger(selected) && Number(selected) >= 0;
    }

    const sourceType = getSourceType(step);
    if (sourceType === 'ordering') {
      return (orderingValues[step.step_id] || []).some((item) => String(item ?? '').trim().length > 0);
    }
    if (sourceType === 'matching') {
      const pairs = matchingValues[step.step_id] || {};
      const config = getMatchingConfig(step);
      return config.leftItems.length > 0
        && config.leftItems.every((left) => Boolean(String(pairs[left] || '').trim()));
    }
    if (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks' || sourceType === 'drag_drop_keywords') {
      const meta = getDragDropMeta(step);
      if (meta.blankCount <= 0) return false;
      const slots = dragDropValues[step.step_id] || [];
      return slots.slice(0, meta.blankCount).every((slot) => String(slot?.token ?? '').trim().length > 0);
    }
    return String(drafts[step.step_id] || '').trim().length > 0;
  }, [drafts, dragDropValues, matchingValues, multiChoice, orderingValues, singleChoice, guidedResponses, getGuidedCardError]);

  const handleSaveReplayStep = async () => {
    if (!assignmentId || !currentStep) return;
    const currentSignalConfig = currentStep.signal_config || {};
    const currentSignalType = String(currentSignalConfig.signal_type || currentSignalConfig.type || '').trim().toLowerCase().replace(/-/g, '_');
    const isGuidedSequence = currentSignalType === 'guided_sequence';
    const reasoningGateConfig = currentSignalConfig.reasoning_gate && typeof currentSignalConfig.reasoning_gate === 'object'
      ? currentSignalConfig.reasoning_gate
      : {};
    const reasoningGateModeRaw = typeof reasoningGateConfig.mode === 'string'
      ? reasoningGateConfig.mode.trim().toLowerCase()
      : '';
    const reasoningGateMode = (
      reasoningGateModeRaw === 'required' || reasoningGateModeRaw === 'optional' || reasoningGateModeRaw === 'off'
    ) ? reasoningGateModeRaw : (isGuidedSequence ? 'off' : (currentSignalConfig.reason_required === false ? 'off' : 'required'));
    const reasoningMinCharsRaw = Number(reasoningGateConfig.min_chars);
    const reasoningMinChars = Number.isInteger(reasoningMinCharsRaw) && reasoningMinCharsRaw >= 0 ? reasoningMinCharsRaw : 15;
    const currentReasoningText = String(reasoningTexts[currentStep.step_id] || '').trim();
    const currentShowOptionalReasoning = Boolean(showOptionalReasoningByStep[currentStep.step_id]);
    const confidenceRequired = typeof currentSignalConfig.confidence_required === 'boolean'
      ? currentSignalConfig.confidence_required
      : !isGuidedSequence;
    const confidenceSelected = confidenceLevels[currentStep.step_id] !== null && confidenceLevels[currentStep.step_id] !== undefined;
    const guidedCards = getGuidedSequenceCards(currentStep);
    if (isGuidedSequence && guidedCards.length === 0) {
      setReplayError('No guided sequence cards are configured for this step.');
      return;
    }
    if (isGuidedSequence) {
      for (let idx = 0; idx < guidedCards.length; idx += 1) {
        const card = guidedCards[idx];
        const candidate = guidedResponses[currentStep.step_id]?.[card.key] || '';
        const errorMessage = getGuidedCardError(card, candidate);
        if (errorMessage) {
          setGuidedCardIndices((prev) => ({ ...prev, [currentStep.step_id]: idx }));
          setReplayError(errorMessage);
          return;
        }
      }
    }
    if (reasoningGateMode === 'required' && currentReasoningText.length < reasoningMinChars) {
      setReplayError(`reasoning_text must be at least ${reasoningMinChars} characters.`);
      return;
    }
    if (reasoningGateMode === 'optional' && currentShowOptionalReasoning && currentReasoningText.length > 0 && currentReasoningText.length < reasoningMinChars) {
      setReplayError(`reasoning_text must be at least ${reasoningMinChars} characters when provided.`);
      return;
    }
    if (confidenceRequired && !confidenceSelected) {
      setReplayError('confidence_level must be between 1 and 5.');
      return;
    }
    if (!hasResponseForStep(currentStep)) {
      setReplayError('Enter a response before saving.');
      return;
    }
    setReplaySaving(true);
    setReplayError('');
    try {
      await assignmentsApi.start(Number(assignmentId));
      await assignmentsApi.submitResponse(Number(assignmentId), {
        step_id: currentStep.step_id,
        response: buildReplayPayload(currentStep),
      });
      await loadDetail();
    } catch (error: any) {
      if (error?.response?.status === 428) {
        navigate('/v2/student', {
          replace: true,
          state: { gate: error?.response?.data?.gate || null },
        });
        return;
      }
      setReplayError(error?.response?.data?.error || 'Could not save this response.');
    } finally {
      setReplaySaving(false);
    }
  };

  const handleSubmitExam = async () => {
    if (!assignmentId) return;
    setExamSubmitting(true);
    setReplayError('');
    try {
      await assignmentsApi.submitExam(Number(assignmentId));
      await loadDetail();
    } catch (error: any) {
      if (error?.response?.status === 428) {
        navigate('/v2/student', {
          replace: true,
          state: { gate: error?.response?.data?.gate || null },
        });
        return;
      }
      setReplayError(error?.response?.data?.error || 'Could not submit exam.');
    } finally {
      setExamSubmitting(false);
    }
  };

  const handleRetakeQuiz = async () => {
    if (!assignmentId) return;
    setRetakeSubmitting(true);
    setReplayError('');
    try {
      await assignmentsApi.retakeQuiz(Number(assignmentId));
      resetReplayAttemptState();
      await loadDetail();
    } catch (error: any) {
      if (error?.response?.status === 428) {
        navigate('/v2/student', {
          replace: true,
          state: { gate: error?.response?.data?.gate || null },
        });
        return;
      }
      setReplayError(error?.response?.data?.error || 'Could not start a new attempt.');
    } finally {
      setRetakeSubmitting(false);
    }
  };

  const handleRequestRetake = async () => {
    if (!assignmentId) return;
    setRetakeSubmitting(true);
    setReplayError('');
    try {
      await assignmentsApi.requestRetake(Number(assignmentId), {
        remediation_response: retakeRemediationResponse.trim(),
      });
      await loadDetail();
    } catch (error: any) {
      setReplayError(error?.response?.data?.error || 'Could not request a retake.');
    } finally {
      setRetakeSubmitting(false);
    }
  };

  const handleAssessmentAttachment = async (file?: File) => {
    if (!file || !assignmentId) return;
    setAttachmentUploading(true);
    setAssessmentDocumentNotice('');
    try {
      await assignmentsApi.uploadAttachment(Number(assignmentId), file, currentStep?.step_id);
      await loadDetail();
      setAssessmentDocumentNotice('Supporting file uploaded.');
    } catch (error: any) {
      setAssessmentDocumentNotice(error?.response?.data?.error || 'Could not upload the supporting file.');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleAssessmentEvidenceDownload = async () => {
    if (!assignmentId) return;
    setAssessmentExporting(true);
    setAssessmentDocumentNotice('');
    try {
      const generated = await exportApi.generateAssessmentEvidence(Number(assignmentId));
      const response = await exportApi.download(Number(generated.export_id));
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = `${(lessonPlanTitle || 'assessment').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_evidence.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
      setAssessmentDocumentNotice('Evidence document downloaded. Upload this PDF to the appropriate Headrush assessment card.');
    } catch (error: any) {
      setAssessmentDocumentNotice(error?.response?.data?.detail || error?.response?.data?.error || 'Could not generate the evidence document.');
    } finally {
      setAssessmentExporting(false);
    }
  };

  const handleConfirmExternalSubmission = async () => {
    if (!assignmentId) return;
    setExternalSubmissionConfirming(true);
    setAssessmentDocumentNotice('');
    try {
      await assignmentsApi.confirmExternalSubmission(Number(assignmentId));
      await loadDetail();
      setAssessmentDocumentNotice('Submission confirmed. Elevate recorded that you uploaded the evidence PDF to Headrush.');
    } catch (error: any) {
      setAssessmentDocumentNotice(error?.response?.data?.error || 'Could not confirm the Headrush submission.');
    } finally {
      setExternalSubmissionConfirming(false);
    }
  };

  if (!detail) {
    const loadingState = (
      <div className={redesignEnabled ? 'max-w-4xl mx-auto' : 'max-w-4xl mx-auto p-6'}>
        <div className={replayError
          ? 'rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'
          : 'text-sm text-gray-500'}
        >
          {replayError || 'Loading assignment...'}
        </div>
      </div>
    );
    return (
      <OptionalAppShell
        enabled={redesignEnabled}
        role="student"
        navItems={studentV2NavItems('work')}
        actions={shellActions}
        title="Make-up Assignment"
      >
        {loadingState}
      </OptionalAppShell>
    );
  }

  if (!replayMode) {
    const evidenceSurface = (
      <div className={redesignEnabled ? 'max-w-4xl mx-auto' : 'max-w-4xl mx-auto p-6'}>
        {!redesignEnabled && shellActions}
        <div className="mt-4 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500">Assignment</p>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">
              {lessonPlanTitle || `Lesson Plan #${detail.lesson_plan}`}
            </h1>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[studentStatus]}`}>
              {studentStatus.replace('_', ' ')}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {sectionName ? `Section: ${sectionName}` : `Section #${detail.section}`}
          </p>
          <div className="mt-2 text-sm text-gray-500">
            <span>Assigned {new Date(detail.assigned_at).toLocaleDateString()}</span>
            {detail.due_at && <span className="ml-3">Due {new Date(detail.due_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {detail.resources?.length ? (
          <div className="mt-4">
            <ResourceToolkit resources={detail.resources} lifecycleKey={`assignment-${detail.id}-evidence`} />
          </div>
        ) : null}

        <div className="mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-gray-900">Required Evidence Steps</h2>
          {detail.steps.length === 0 && (
            <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This assignment has no evidence-required steps configured yet.
            </div>
          )}
          <div className="mt-4 space-y-4">
            {detail.steps.map((step) => (
              <div key={step.step_id} className="border border-slate-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Step {step.order}</p>
                    <h3 className="text-base font-semibold text-gray-900">{step.title}</h3>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${step.has_current_evidence ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {step.has_current_evidence ? 'Evidence submitted' : 'Not submitted'}
                  </span>
                </div>
                {step.latest_evidence ? (
                  <div className="mt-3 text-sm text-gray-600">
                    <div className="text-xs uppercase text-slate-400">Latest evidence</div>
                    <div className="mt-1">{step.latest_evidence.status} · {new Date(step.latest_evidence.submitted_at).toLocaleDateString()}</div>
                    {step.latest_evidence.content?.text && (
                      <div className="mt-2 text-gray-700">{step.latest_evidence.content.text}</div>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-gray-500">No evidence submitted yet.</div>
                )}
                <div className="mt-4">
                  <label className="text-xs uppercase text-slate-400">Your evidence</label>
                  <textarea
                    className="mt-2 w-full rounded-md border border-slate-200 p-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    value={drafts[step.step_id] || ''}
                    onChange={(event) => setDrafts((prev) => ({ ...prev, [step.step_id]: event.target.value }))}
                    disabled={submitting[step.step_id]}
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {normalize(drafts[step.step_id]) === normalize(step.latest_evidence?.content?.text) ? 'No changes to submit.' : 'Ready to submit.'}
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
                      onClick={() => handleSubmitEvidence(step)}
                      disabled={submitting[step.step_id] || normalize(drafts[step.step_id]) === normalize(step.latest_evidence?.content?.text)}
                    >
                      {step.latest_evidence ? 'Submit updated evidence' : 'Submit evidence'}
                    </button>
                  </div>
                  {errors[step.step_id] && (
                    <div className="mt-2 text-xs text-rose-500">{errors[step.step_id]}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
    return (
      <OptionalAppShell
        enabled={redesignEnabled}
        role="student"
        navItems={studentV2NavItems('work')}
        actions={shellActions}
        title="Make-up Assignment"
      >
        {evidenceSurface}
      </OptionalAppShell>
    );
  }

  const options = currentStep ? getOptions(currentStep) : [];
  const signalType = currentStep ? normalizeSignalType(currentStep) : '';
  const selectRaw = Number(currentStep?.signal_config?.select || 0);
  const isMultiSelect = signalType === 'mcq_multi' || (Number.isFinite(selectRaw) && selectRaw > 1);
  const requiredSelections = Number.isFinite(selectRaw) && selectRaw > 0 ? selectRaw : (isMultiSelect ? 2 : 1);
  const currentSourceType = currentStep ? getSourceType(currentStep) : '';
  const isOrderingStep = currentSourceType === 'ordering';
  const isMatchingStep = currentSourceType === 'matching';
  const isDragDropStep = ['drag_drop_code', 'drag_drop_fill_blanks', 'drag_drop_keywords'].includes(currentSourceType);
  const currentOrderingItems = currentStep ? (orderingValues[currentStep.step_id] || []) : [];
  const currentMatchingPairs = currentStep ? (matchingValues[currentStep.step_id] || {}) : {};
  const currentMatchingConfig = currentStep ? getMatchingConfig(currentStep) : { leftItems: [], rightItems: [], expectedPairs: {} };
  const currentSignalConfig = currentStep?.signal_config || {};
  const currentSignalPrompt = currentStep ? getSignalPrompt(currentStep) : '';
  const isGuidedSequenceSignal = signalType === 'guided_sequence';
  const guidedSubmitLabel = typeof currentSignalConfig.submit_label === 'string' && currentSignalConfig.submit_label.trim()
    ? currentSignalConfig.submit_label.trim()
    : 'Save Response';
  const guidedSequenceCards = currentStep ? getGuidedSequenceCards(currentStep) : [];
  const currentGuidedResponses = currentStep ? (guidedResponses[currentStep.step_id] || {}) : {};
  const currentGuidedCardIndexRaw = currentStep ? (guidedCardIndices[currentStep.step_id] || 0) : 0;
  const currentGuidedCardIndex = guidedSequenceCards.length > 0
    ? Math.max(0, Math.min(currentGuidedCardIndexRaw, guidedSequenceCards.length - 1))
    : 0;
  const currentGuidedCard = guidedSequenceCards[currentGuidedCardIndex] || null;
  const currentGuidedCardValue = currentGuidedCard ? (currentGuidedResponses[currentGuidedCard.key] || '') : '';
  const currentGuidedCardError = currentGuidedCard
    ? getGuidedCardError(currentGuidedCard, currentGuidedCardValue)
    : '';
  const guidedRequiredComplete = guidedSequenceCards.length > 0 && guidedSequenceCards.every((card) => {
    const candidate = currentGuidedResponses[card.key] || '';
    return !getGuidedCardError(card, candidate);
  });
  const reasoningGateConfig = currentSignalConfig.reasoning_gate && typeof currentSignalConfig.reasoning_gate === 'object'
    ? currentSignalConfig.reasoning_gate
    : {};
  const reasoningGateModeRaw = typeof reasoningGateConfig.mode === 'string'
    ? reasoningGateConfig.mode.trim().toLowerCase()
    : '';
  const reasoningGateMode = (
    reasoningGateModeRaw === 'required' || reasoningGateModeRaw === 'optional' || reasoningGateModeRaw === 'off'
  ) ? reasoningGateModeRaw : (isGuidedSequenceSignal ? 'off' : (currentSignalConfig.reason_required === false ? 'off' : 'required'));
  const reasoningMinCharsRaw = Number(reasoningGateConfig.min_chars);
  const reasoningMinChars = Number.isInteger(reasoningMinCharsRaw) && reasoningMinCharsRaw >= 0 ? reasoningMinCharsRaw : 15;
  const reasoningRequired = reasoningGateMode === 'required';
  const reasoningOptional = reasoningGateMode === 'optional';
  const currentShowOptionalReasoning = currentStep ? Boolean(showOptionalReasoningByStep[currentStep.step_id]) : false;
  const shouldShowReasoningTextarea = reasoningRequired || (reasoningOptional && currentShowOptionalReasoning);
  const currentReasoningText = currentStep ? String(reasoningTexts[currentStep.step_id] || '') : '';
  const trimmedReasoningText = currentReasoningText.trim();
  const hasValidReasoning = !shouldShowReasoningTextarea || trimmedReasoningText.length >= reasoningMinChars;
  const confidenceRequired = typeof currentSignalConfig.confidence_required === 'boolean'
    ? currentSignalConfig.confidence_required
    : !isGuidedSequenceSignal;
  const currentConfidenceLevel = currentStep ? (confidenceLevels[currentStep.step_id] ?? null) : null;
  const confidenceSelected = currentConfidenceLevel !== null && currentConfidenceLevel !== undefined;
  const currentAnswerLines = currentStep
    ? (
      Array.isArray(currentStep.answer_key_lines) && currentStep.answer_key_lines.length > 0
        ? currentStep.answer_key_lines
        : getAnswerRevealLines(currentStep)
    )
    : [];
  const revealAnswerGate = Boolean(currentStep?.has_response || studentStatus === 'completed');
  const showAnswerReveal = Boolean(revealAnswerGate && currentAnswerLines.length > 0);
  const currentAnswerCorrect = currentStep?.latest_response?.is_correct;
  const currentDragDropMeta = currentStep && isDragDropStep ? getDragDropMeta(currentStep) : null;
  const currentDragDropSlots = currentStep ? (dragDropValues[currentStep.step_id] || []) : [];
  const replayInteractionLocked = studentStatus === 'completed';
  const currentStepAcceptsResponse = currentStep ? stepAcceptsReplayResponse(currentStep) : false;
  const currentObservationConfig = currentStep ? getObservationConfig(currentStep) : null;
  const currentStepHasResponse = currentStep && currentStepAcceptsResponse ? hasResponseForStep(currentStep) : false;
  const replayRunId = typeof detail?.run_id === 'number' ? detail.run_id : null;
  const replayRunIsTestMode = Boolean(detail?.run_is_test_mode || detail?.delivery_mode === 'replay_exam');
  const replayHelpStep: LessonStep | null = currentStep
    ? {
      id: currentStep.step_id,
      lesson_plan: detail?.lesson_plan || 0,
      title: currentStep.title,
      order: currentStep.order,
      stage_tag: currentStep.stage_tag || null,
      counts_as_evidence: currentStep.counts_as_evidence,
      step_kind: currentStep.step_kind,
      content: currentStep.content || {},
      evidence_config: {},
      signal_config: currentStep.signal_config || {},
    }
    : null;
  const normalizedReplayStepKind = String(currentStep?.step_kind || '').trim().toLowerCase();
  const replayStepIsAssessmentLike = normalizedReplayStepKind === 'assessment' || normalizedReplayStepKind === 'exit_ticket';
  const replayStepHelpEnabled = isReplayStepHelpEnabled(replayHelpStep);
  const replayHasUdlContext = Boolean(
    detail?.id
    && currentStep?.step_id
    && currentStepAcceptsResponse
    && hasStepContent(replayHelpStep),
  );
  const replayHelpSurfaceApi = detail
    ? {
      getMyState: async () => ({ teacher_action_state: {} }),
      getSuggestions: () => assignmentsApi.udlSuggestions(detail.id, currentStep?.step_id),
      assist: (payload: { step_id: number; assist_mode: 'simplify' | 'example' | 'hint' | 'step_breakdown' }) =>
        assignmentsApi.udlAssist(detail.id, payload),
      followup: (payload: {
        step_id: number;
        question: string;
        assist_mode?: 'simplify' | 'example' | 'hint' | 'step_breakdown';
        prior_help_title?: string;
        prior_help_content?: string;
      }) => assignmentsApi.udlFollowup(detail.id, payload),
    }
    : undefined;
  const showFullReplayUdl = Boolean(
    replayMode
    && !replayRunIsTestMode
    && currentStepAcceptsResponse
    && replayHasUdlContext
    && !replayStepIsAssessmentLike
    && replayStepHelpEnabled
  );
  const showLimitedReplayUdl = Boolean(
    replayMode
    && replayHasUdlContext
    && !showFullReplayUdl
    && (replayRunIsTestMode || replayStepIsAssessmentLike || !replayStepHelpEnabled),
  );
  const previousAnswersVisibleItems = previousAnswersData?.items || [];
  const shouldShowPreviousAnswersHelper = Boolean(
    currentStepAcceptsResponse
    && previousAnswersData
    && String(previousAnswersData.mode || '').trim().toLowerCase() !== 'off'
    && previousAnswersVisibleItems.length > 0,
  );
  const previousAnswersTitle = String(previousAnswersData?.title || '').trim() || 'My previous answers';
  const canInsertIntoTextSignal = !isGuidedSequenceSignal && !options.length && !isOrderingStep && !isMatchingStep && !isDragDropStep;
  const canInsertIntoGuidedCard = isGuidedSequenceSignal && Boolean(currentGuidedCard?.key);
  const canInsertPreviousAnswers = Boolean(
    previousAnswersData?.allow_insert
    && !replayInteractionLocked
    && (canInsertIntoTextSignal || canInsertIntoGuidedCard),
  );
  const insertPreviousAnswer = (responseText: string) => {
    if (!currentStep) return;
    const snippet = String(responseText || '').trim();
    if (!snippet) return;
    if (canInsertIntoGuidedCard && currentGuidedCard?.key) {
      setGuidedResponses((prev) => {
        const currentStepResponses = prev[currentStep.step_id] || {};
        const existing = String(currentStepResponses[currentGuidedCard.key] || '').trim();
        return {
          ...prev,
          [currentStep.step_id]: {
            ...currentStepResponses,
            [currentGuidedCard.key]: existing ? `${existing}\n${snippet}` : snippet,
          },
        };
      });
      return;
    }
    if (canInsertIntoTextSignal) {
      setDrafts((prev) => {
        const existing = String(prev[currentStep.step_id] || '').trim();
        return {
          ...prev,
          [currentStep.step_id]: existing ? `${existing}\n${snippet}` : snippet,
        };
      });
    }
  };
  const dragDropRevealGate = Boolean(
    isDragDropStep
    && currentStep?.answer_key?.kind === 'drag_drop'
    && (currentStep?.has_response || studentStatus === 'completed'),
  );
  const dragDropAnswerTokens = Array.isArray(currentStep?.answer_key?.answer_tokens)
    ? (currentStep?.answer_key?.answer_tokens as any[])
    : [];
  const dragDropExpectedTokens = dragDropRevealGate
    ? dragDropAnswerTokens.map((item: any) => {
      const token = String(item ?? '').trim();
      return token.length > 0 ? token : null;
    })
    : [];
  const getStepStatusBadge = (scoreState: StepScoreState): { label: string; className: string } | null => {
    if (scoreState === 'correct') {
      return {
        label: 'Correct',
        className: 'bg-emerald-100 text-emerald-800',
      };
    }
    if (scoreState === 'incorrect') {
      return {
        label: 'Missed',
        className: 'bg-rose-100 text-rose-800',
      };
    }
    if (scoreState === 'saved_unscored') {
      return {
        label: 'Saved',
        className: 'bg-slate-100 text-slate-700',
      };
    }
    return null;
  };

  const replaySurface = (
    <div className={redesignEnabled ? 'max-w-5xl mx-auto' : 'max-w-5xl mx-auto p-6'}>
      {!redesignEnabled && shellActions}
      <div className="mt-4 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <p className="text-xs uppercase tracking-wide text-slate-500">
          {isPractice ? 'Independent work' : isAssessment ? `${String(detail.assessment_stage || '').toUpperCase()} Independent Assessment` : ['makeup', 'practice'].includes(detail.purpose || '') ? (isExamMode ? 'Make-up Quiz' : 'Make-up Lesson') : isExamMode ? 'Replay Quiz' : 'Replay Lesson'}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">
            {lessonPlanTitle || `Lesson Plan #${detail.lesson_plan}`}
          </h1>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[studentStatus]}`}>
            {studentStatus.replace('_', ' ')}
          </span>
          {replayMode && (
            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
              Attempt {currentAttemptNumber}
            </span>
          )}
          {canRetakeReplay && (
            <button
              type="button"
              className="ml-auto rounded border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              onClick={handleRetakeQuiz}
              disabled={retakeSubmitting}
            >
              {retakeSubmitting ? retakeLoadingLabel : retakeButtonLabel}
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500">
          {sectionName ? `Section: ${sectionName}` : `Section #${detail.section}`}
        </p>
        <div className="mt-2 text-sm text-gray-500">
          <span>Assigned {new Date(detail.assigned_at).toLocaleDateString()}</span>
          {detail.due_at && <span className="ml-3">Due {new Date(detail.due_at).toLocaleDateString()}</span>}
        </div>
        {['makeup', 'practice'].includes(detail.purpose || '') && detail.makeup_review?.status === 'needs_revision' && (
          <section className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950" role="status">
            <p className="font-semibold">{isPractice ? 'Your teacher requested a revision.' : 'Your teacher sent this make-up back for revision.'}</p>
            <p className="mt-1 whitespace-pre-wrap">{detail.makeup_review.note}</p>
            <p className="mt-2">A new attempt is open. Complete the lesson again and resubmit it for review.</p>
          </section>
        )}
        {['makeup', 'practice'].includes(detail.purpose || '') && detail.makeup_review?.status === 'pending' && studentStatus === 'completed' && (
          <p className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">{isPractice ? 'Activity finished. You can continue to your next available work.' : 'Make-up submitted. Your teacher has not reviewed it yet.'}</p>
        )}
        {['makeup', 'practice'].includes(detail.purpose || '') && detail.makeup_review?.status === 'approved' && (
          <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{isPractice ? 'Activity reviewed' : 'Make-up reviewed'}{detail.makeup_review.note ? `: ${detail.makeup_review.note}` : '.'}</p>
        )}
        {isAssessment && studentStatus === 'in_progress' && detail.retake_workflow?.current_attempt_context && (
          <section className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-semibold">Your teacher opened a new assessment attempt.</p>
            <p className="mt-1 whitespace-pre-wrap"><span className="font-medium">Reason:</span> {detail.retake_workflow.current_attempt_context.teacher_note}</p>
            <p className="mt-2">Your previous attempt remains saved. Complete and submit this new attempt.</p>
          </section>
        )}
        {isAssessment && studentStatus === 'completed' && detail.retake_workflow && (
          <section className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-semibold">Assessment retake</p>
            {detail.retake_workflow.policy === 'disabled' && (
              <p className="mt-1">A retake is not currently available. Ask your teacher what evidence or next step is needed.</p>
            )}
            {detail.retake_workflow.policy === 'automatic' && (
              <p className="mt-1">You may start another attempt when you are ready.</p>
            )}
            {detail.retake_workflow.policy === 'teacher_approval' && (
              <div className="mt-2 space-y-3">
                {detail.retake_workflow.remediation_instructions && (
                  <div>
                    <p className="font-semibold">Complete before requesting another attempt:</p>
                    <p className="mt-1 whitespace-pre-wrap">{detail.retake_workflow.remediation_instructions}</p>
                  </div>
                )}
                {(detail.retake_workflow.request?.required_remediation || []).length > 0 && (
                  <div className="rounded border border-amber-300 bg-white p-3">
                    <p className="font-semibold">Your teacher requires this preparation:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {(detail.retake_workflow.request?.required_remediation || []).map((requirement, index) => (
                        <li key={`retake-requirement-${index}`}>{requirement}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.retake_workflow.request?.status === 'requested' && (
                  <p>Your request is waiting for teacher review.</p>
                )}
                {detail.retake_workflow.request?.status === 'approved' && (
                  <p>Your teacher approved another attempt. Use the retake button above when you are ready.</p>
                )}
                {detail.retake_workflow.request?.status === 'denied' && (
                  <div>
                    <p>Your teacher asked you to complete more preparation before another request.</p>
                    {detail.retake_workflow.request.teacher_note && (
                      <p className="mt-1 font-medium">Teacher note: {detail.retake_workflow.request.teacher_note}</p>
                    )}
                  </div>
                )}
                {detail.retake_workflow.can_request && (
                  <div>
                    <label className="block font-medium" htmlFor="retake-remediation-response">
                      {detail.retake_workflow.remediation_required ? 'Explain what you completed' : 'Preparation note (optional)'}
                    </label>
                    <textarea
                      id="retake-remediation-response"
                      className="mt-1 w-full rounded border border-amber-300 bg-white px-3 py-2"
                      rows={3}
                      value={retakeRemediationResponse}
                      onChange={(event) => setRetakeRemediationResponse(event.target.value)}
                    />
                    <button
                      type="button"
                      className="mt-2 rounded bg-amber-700 px-3 py-2 font-semibold text-white disabled:opacity-50"
                      disabled={retakeSubmitting || (detail.retake_workflow.remediation_required && !retakeRemediationResponse.trim())}
                      onClick={() => { void handleRequestRetake(); }}
                    >
                      {retakeSubmitting ? 'Sending request…' : 'Request teacher approval'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
        {isAssessment && !showObjectiveResults && studentStatus === 'completed' && (
          <p className="mt-3 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Your objective results are saved for teacher review. Your teacher will guide you on readiness and next steps.
          </p>
        )}
        {detail.progress && (
          <div className="mt-3 space-y-1 text-sm text-slate-700">
            <div>
              Progress: {detail.progress.answered_count}/{detail.progress.total_steps} answered
              {shouldShowReplayScoredCount && (
                <span className="ml-3">Scored: {detail.progress.correct_count}/{detail.progress.scorable_count}</span>
              )}
            </div>
            {shouldShowReplayScoreSummary && (
              <div>
                Score: {detail.progress?.correct_count}/{detail.progress?.scorable_count} ({detail.progress?.score_percent}%)
              </div>
            )}
          </div>
        )}
      </div>

      {detail.resources?.length ? (
        <div className="mt-4">
          <ResourceToolkit
            resources={detail.resources}
            lifecycleKey={`assignment-${detail.id}-step-${currentStep?.step_id || 'none'}`}
            sessionKey={`assignment-${detail.id}`}
          />
        </div>
      ) : null}

      {isAssessment && (
        <section className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
          <h2 className="text-sm font-semibold text-indigo-950">Supporting artifacts</h2>
          <p className="mt-1 text-sm text-indigo-800">Add files that should be listed in your final Headrush evidence document. Maximum file size: 25 MB.</p>
          {currentStep?.artifact_requirement === 'apply_required' && (
            <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">A supporting file is required for this task before the assessment can be completed.</p>
          )}
          <label className="mt-3 inline-flex cursor-pointer rounded border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700">
            {attachmentUploading ? 'Uploading…' : 'Upload supporting file'}
            <input
              type="file"
              className="sr-only"
              disabled={attachmentUploading}
              onChange={(event) => {
                void handleAssessmentAttachment(event.target.files?.[0]);
                event.currentTarget.value = '';
              }}
            />
          </label>
          {(detail.attachments || []).length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-indigo-900">
              {(detail.attachments || []).map((attachment) => (
                <li key={attachment.id}>{attachment.original_name}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {detail.steps.length === 0 && (
        <div className="mt-6 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This replay assignment has no steps configured.
        </div>
      )}

      {currentStep && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-1 bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">Steps</p>
            {showMissedFilterControls && (
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`rounded border px-2.5 py-1 text-xs font-semibold ${
                    stepFilter === 'all'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                      : 'border-slate-300 bg-white text-slate-700'
                  }`}
                  onClick={() => setStepFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`rounded border px-2.5 py-1 text-xs font-semibold ${
                    stepFilter === 'missed'
                      ? 'border-rose-400 bg-rose-50 text-rose-900'
                      : 'border-slate-300 bg-white text-slate-700'
                  }`}
                  onClick={() => setStepFilter('missed')}
                >
                  Missed only
                </button>
              </div>
            )}
            <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
              {showMissedEmptyState ? (
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  No missed scored questions.
                </div>
              ) : (
                visibleReplaySteps.map((step) => {
                  const scoreState = stepScoreStateById[step.step_id] || 'unanswered';
                  const badge = getStepStatusBadge(scoreState);
                  const isActiveStep = currentStep.step_id === step.step_id;
                  const stepButtonClass = isActiveStep
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                    : scoreState === 'incorrect'
                      ? 'border-rose-200 bg-rose-50 text-rose-900'
                      : scoreState === 'correct'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                        : scoreState === 'saved_unscored'
                          ? 'border-slate-300 bg-slate-50 text-slate-700'
                          : 'border-slate-200 bg-white text-slate-700';
                  return (
                    <button
                      key={step.step_id}
                      type="button"
                      onClick={() => setActiveStepId(step.step_id)}
                      className={`w-full text-left rounded border px-3 py-2 text-sm ${stepButtonClass}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold">
                          {getObservationConfig(step) ? getStudentStepTitle(step) : `#${step.order} ${getStudentStepTitle(step)}`}
                        </div>
                        {badge && (
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}>
                            {badge.label}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="lg:col-span-3 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
            {currentObservationConfig ? (
              <section className="rounded-lg border border-violet-200 bg-violet-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-700">Teacher-verified evidence</p>
                <h2 className="mt-1 text-2xl font-semibold text-violet-950">{getStudentStepTitle(currentStep)}</h2>
                <p className="mt-2 text-sm text-violet-900">
                  No additional written response is required here. Your teacher records this evidence after observing your {currentObservationConfig.kind === 'verbal' ? 'explanation or demonstration' : 'hands-on work'}.
                </p>
              </section>
            ) : (
              <StepRenderer
                title={currentStep.title}
                stageTag={currentStep.stage_tag}
                stepKind={currentStep.step_kind}
                content={currentStep.content}
              />
            )}

            {currentSignalPrompt && (
              <div className="mt-5 rounded-md border border-indigo-100 bg-indigo-50/50 px-3 py-2">
                <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">Question</p>
                <p className="mt-1 text-sm text-indigo-900 whitespace-pre-wrap">{currentSignalPrompt}</p>
              </div>
            )}

            {!currentObservationConfig && <div className="mt-5 space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step Help</p>
              <HelpTab
                runId={replayRunId}
                currentStep={replayHelpStep}
                runStatus={studentStatus}
                stepKey={`${detail?.id ?? 'none'}:${replayRunId ?? 'none'}:${currentStep?.step_id ?? 'none'}:${showFullReplayUdl ? 'full' : showLimitedReplayUdl ? 'limited' : 'fallback'}`}
                supportMode={showFullReplayUdl ? 'full' : showLimitedReplayUdl ? 'limited' : 'fallback'}
                limitedMessage={!replayStepHelpEnabled ? 'Help is off for this step.' : replayRunIsTestMode || replayStepIsAssessmentLike ? 'Help is off for this check.' : null}
                hasStepContextOverride={replayHasUdlContext}
                surfaceApi={replayHelpSurfaceApi}
              />
            </div>}

            {currentStepAcceptsResponse && (previousAnswersLoading || previousAnswersError || shouldShowPreviousAnswersHelper) && (
              <section className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">{previousAnswersTitle}</p>
                  {shouldShowPreviousAnswersHelper && (
                    <button
                      type="button"
                      className="text-xs font-semibold text-indigo-700 hover:text-indigo-900"
                      onClick={() => setPreviousAnswersOpen((prev) => !prev)}
                    >
                      {previousAnswersOpen ? 'Hide previous answers' : 'Show my previous answers'}
                    </button>
                  )}
                </div>
                {previousAnswersLoading && (
                  <p className="text-xs text-slate-600">Loading previous answers...</p>
                )}
                {!previousAnswersLoading && previousAnswersError && (
                  <p className="text-xs text-rose-700">{previousAnswersError}</p>
                )}
                {!previousAnswersLoading && shouldShowPreviousAnswersHelper && previousAnswersOpen && (
                  <div className="space-y-2">
                    {previousAnswersVisibleItems.map((item) => (
                      <div key={`previous-answer-${item.step_id}`} className="rounded border border-indigo-100 bg-white px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-indigo-900">{item.label}</p>
                          {canInsertPreviousAnswers && (
                            <button
                              type="button"
                              className="text-xs font-semibold text-indigo-700 hover:text-indigo-900"
                              onClick={() => insertPreviousAnswer(item.response_text)}
                            >
                              Insert
                            </button>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{item.response_text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {isGuidedSequenceSignal ? (
              <section className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
                {guidedSequenceCards.length === 0 && (
                  <p className="text-sm text-amber-700">No guided sequence cards are configured for this step.</p>
                )}
                {currentGuidedCard && (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">
                        Step {currentGuidedCardIndex + 1} of {guidedSequenceCards.length}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{currentGuidedCard.prompt}</p>
                      {currentGuidedCard.required && (
                        <p className="mt-1 text-xs text-slate-500">Required</p>
                      )}
                    </div>
                    {currentGuidedCard.template && (
                      <pre className="rounded border border-indigo-100 bg-white p-3 text-xs text-slate-800 overflow-x-auto whitespace-pre-wrap">
                        <code>{currentGuidedCard.template}</code>
                      </pre>
                    )}
                    {currentGuidedCard.input_type === 'select' ? (
                      <fieldset className="space-y-2">
                        <legend className="sr-only">{currentGuidedCard.prompt}</legend>
                        {currentGuidedCard.options.map((option) => (
                          <label key={option.value} className="flex cursor-pointer items-start gap-3 rounded border border-indigo-100 bg-white px-3 py-2 text-sm text-slate-900">
                            <input
                              type="radio"
                              name={`guided-${currentStep.step_id}-${currentGuidedCard.key}`}
                              value={option.value}
                              checked={currentGuidedCardValue === option.value}
                              onChange={(event) => setGuidedResponses((prev) => ({
                                ...prev,
                                [currentStep.step_id]: {
                                  ...(prev[currentStep.step_id] || {}),
                                  [currentGuidedCard.key]: event.target.value,
                                },
                              }))}
                              disabled={replayInteractionLocked}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </fieldset>
                    ) : currentGuidedCard.input_type === 'textarea' ? (
                      <textarea
                        className="w-full rounded-md border border-slate-200 p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={4}
                        value={currentGuidedCardValue}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setGuidedResponses((prev) => ({
                            ...prev,
                            [currentStep.step_id]: {
                              ...(prev[currentStep.step_id] || {}),
                              [currentGuidedCard.key]: nextValue,
                            },
                          }));
                        }}
                        placeholder={currentGuidedCard.placeholder || 'Type your response'}
                        disabled={replayInteractionLocked}
                      />
                    ) : (
                      <input
                        type="text"
                        className="w-full rounded-md border border-slate-200 p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={currentGuidedCardValue}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setGuidedResponses((prev) => ({
                            ...prev,
                            [currentStep.step_id]: {
                              ...(prev[currentStep.step_id] || {}),
                              [currentGuidedCard.key]: nextValue,
                            },
                          }));
                        }}
                        placeholder={currentGuidedCard.placeholder || 'Type your response'}
                        disabled={replayInteractionLocked}
                      />
                    )}
                    {currentGuidedCardError && (
                      <p className="text-xs text-rose-700">{currentGuidedCardError}</p>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-50"
                        onClick={() => setGuidedCardIndices((prev) => ({ ...prev, [currentStep.step_id]: Math.max(0, currentGuidedCardIndex - 1) }))}
                        disabled={replayInteractionLocked || currentGuidedCardIndex === 0}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-50"
                        onClick={() => setGuidedCardIndices((prev) => ({
                          ...prev,
                          [currentStep.step_id]: Math.min(guidedSequenceCards.length - 1, currentGuidedCardIndex + 1),
                        }))}
                        disabled={replayInteractionLocked || currentGuidedCardIndex >= guidedSequenceCards.length - 1}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </section>
            ) : options.length > 0 ? (
              <div className="mt-6 space-y-2">
                <p className="text-sm font-semibold text-slate-700">
                  {isMultiSelect ? 'Select all that apply' : 'Choose one answer'}
                </p>
                {options.map((option, index) => (
                  <label key={`${currentStep.step_id}-${index}`} className="flex items-start gap-2 rounded border border-slate-200 px-3 py-2">
                    <input
                      type={isMultiSelect ? 'checkbox' : 'radio'}
                      name={`step-${currentStep.step_id}`}
                      className="mt-1"
                      checked={isMultiSelect
                        ? (multiChoice[currentStep.step_id] || []).includes(index)
                        : singleChoice[currentStep.step_id] === index}
                      onChange={() => {
                        if (isMultiSelect) {
                          toggleMultiChoice(currentStep.step_id, index);
                        } else {
                          setSingleChoice((prev) => ({ ...prev, [currentStep.step_id]: index }));
                        }
                      }}
                      disabled={replayInteractionLocked}
                    />
                    <span className="text-sm text-slate-700">{option}</span>
                  </label>
                ))}
                {isMultiSelect && !replayInteractionLocked && (
                  <p className="text-xs text-slate-500">
                    Select {requiredSelections} option{requiredSelections === 1 ? '' : 's'}.
                  </p>
                )}
              </div>
            ) : isOrderingStep ? (
              <div className="mt-6"><OrderingResponseEditor items={currentOrderingItems} disabled={replayInteractionLocked} emptyMessage="No ordering items found for this step." onChange={(items) => setOrderingValues((previous) => ({ ...previous, [currentStep.step_id]: items }))} /></div>
            ) : isMatchingStep ? (
              <div className="mt-6"><MatchingResponseEditor leftItems={currentMatchingConfig.leftItems} rightItems={currentMatchingConfig.rightItems} value={currentMatchingPairs} seed={String(currentStep.step_id)} disabled={replayInteractionLocked} emptyMessage="Matching options are not configured for this step." onChange={(value) => setMatchingValues((previous) => ({ ...previous, [currentStep.step_id]: value }))} /></div>
            ) : isDragDropStep && currentDragDropMeta ? (
              <div className="mt-6">
                <DragDropPrompt
                  mode={currentDragDropMeta.mode}
                  promptLines={currentDragDropMeta.promptLines}
                  blankCount={currentDragDropMeta.blankCount}
                  slots={currentDragDropSlots}
                  bankTokens={currentDragDropMeta.bankTokens}
                  interactionLocked={replayInteractionLocked}
                  revealLocked={dragDropRevealGate}
                  revealGate={dragDropRevealGate}
                  expectedTokens={dragDropRevealGate ? dragDropExpectedTokens : undefined}
                  onDropItem={(slotIndex, item) => handleDropDragDropItem(currentStep.step_id, slotIndex, item)}
                  onClear={(slotIndex) => {
                    if (replayInteractionLocked || dragDropRevealGate) return;
                    setDragDropValues((prev) => ({
                      ...prev,
                      [currentStep.step_id]: (prev[currentStep.step_id] || []).map((value, idx) => (idx === slotIndex ? null : value)),
                    }));
                  }}
                  onClickBankToken={(token) => {
                    if (replayInteractionLocked || dragDropRevealGate) return;
                    const nextSlot = currentDragDropSlots.findIndex((value) => value === null);
                    if (nextSlot < 0) return;
                    placeDragDropToken(currentStep.step_id, nextSlot, { source: 'bank', token }, null);
                  }}
                />
              </div>
            ) : currentStepAcceptsResponse ? (
              <div className="mt-6">
                <label className="text-sm font-semibold text-slate-700">Your response</label>
                <textarea
                  className="mt-2 w-full rounded-md border border-slate-200 p-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={4}
                  value={drafts[currentStep.step_id] || ''}
                  onChange={(event) => setDrafts((prev) => ({ ...prev, [currentStep.step_id]: event.target.value }))}
                  disabled={replayInteractionLocked}
                />
              </div>
            ) : (
              <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                <p className="text-sm text-slate-700">
                  This step is informational only. Read it, then move to the next response step.
                </p>
              </div>
            )}

            {currentStepAcceptsResponse && (reasoningRequired || reasoningOptional) && (
              <section className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">Reasoning gate</p>
                  {reasoningRequired && (
                    <p className="mt-2 text-sm text-indigo-900">
                      Explain why you chose your answer ({reasoningMinChars}+ characters).
                    </p>
                  )}
                </div>
                {reasoningOptional && (
                  <label className="flex items-center gap-2 text-sm text-indigo-900">
                    <input
                      type="checkbox"
                      checked={currentShowOptionalReasoning}
                      onChange={(event) => setShowOptionalReasoningByStep((prev) => ({
                        ...prev,
                        [currentStep.step_id]: event.target.checked,
                      }))}
                      disabled={replayInteractionLocked}
                    />
                    Add an explanation (optional)
                  </label>
                )}
                {shouldShowReasoningTextarea && (
                  <textarea
                    className="w-full rounded-md border border-slate-200 p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    value={currentReasoningText}
                    onChange={(event) => setReasoningTexts((prev) => ({
                      ...prev,
                      [currentStep.step_id]: event.target.value,
                    }))}
                    placeholder="Explain your reasoning"
                    disabled={replayInteractionLocked}
                  />
                )}
              </section>
            )}

            {currentStepAcceptsResponse && confidenceRequired && (
              <section className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">How confident are you?</p>
                  <p className="mt-2 text-sm text-indigo-900">Select one level before saving.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 1, label: '1 - Guessing' },
                    { value: 2, label: '2 - Not sure' },
                    { value: 3, label: '3 - Somewhat sure' },
                    { value: 4, label: '4 - Mostly confident' },
                    { value: 5, label: '5 - Very confident' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setConfidenceLevels((prev) => ({ ...prev, [currentStep.step_id]: option.value }))}
                      className={`px-3 py-1.5 rounded border text-sm font-semibold ${
                        currentConfidenceLevel === option.value
                          ? 'border-indigo-500 bg-white text-indigo-700'
                          : 'border-indigo-200 bg-white text-indigo-800'
                      }`}
                      disabled={replayInteractionLocked}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {showAnswerReveal && (
              <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-700">Answer</p>
                  {typeof currentAnswerCorrect === 'boolean' && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${currentAnswerCorrect ? 'bg-emerald-200 text-emerald-900' : 'bg-amber-100 text-amber-800'}`}>
                      {currentAnswerCorrect ? 'Correct' : 'Not correct yet'}
                    </span>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  {currentAnswerLines.map((line, idx) => (
                    <p key={`answer-line-${idx}`} className="text-sm text-emerald-900 whitespace-pre-wrap">{line}</p>
                  ))}
                </div>
              </div>
            )}

            {studentStatus !== 'completed' && currentAnswerCorrect === false && currentStep?.explanation_supported && (
              renderAiPanel(currentStep.step_id, currentStep.explanation_supported, currentStep.ai_thread_preview)
            )}

            {replayError && (
              <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {replayError}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {currentStepAcceptsResponse && (
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
                  onClick={handleSaveReplayStep}
                  disabled={replaySaving || studentStatus === 'completed' || !currentStepHasResponse}
                >
                  {replaySaving ? 'Saving…' : guidedSubmitLabel}
                </button>
              )}
              <button
                type="button"
                className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-50"
                onClick={() => {
                  if (currentStepIndex > 0) {
                    setActiveStepId(visibleReplaySteps[currentStepIndex - 1].step_id);
                  }
                }}
                disabled={currentStepIndex <= 0}
              >
                Previous
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-50"
                onClick={() => {
                  if (currentStepIndex >= 0 && currentStepIndex < visibleReplaySteps.length - 1) {
                    setActiveStepId(visibleReplaySteps[currentStepIndex + 1].step_id);
                  }
                }}
                disabled={currentStepIndex < 0 || currentStepIndex >= visibleReplaySteps.length - 1}
              >
                Next
              </button>
              {isExamMode && studentStatus !== 'completed' && (
                <button
                  type="button"
                  className="ml-auto px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
                  onClick={handleSubmitExam}
                  disabled={examSubmitting}
                >
                  {examSubmitting ? 'Submitting…' : 'Submit Exam For Score'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isAssessment && studentStatus === 'completed' && (
        <section className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Assessment complete</p>
          <h2 className="mt-1 text-xl font-semibold text-emerald-950">Submit your evidence to Headrush</h2>
          <p className="mt-2 text-sm text-emerald-900">
            Follow every step below. Your work is not submitted to Headrush automatically.
          </p>
          <p className="mt-3 rounded border border-emerald-300 bg-white px-3 py-2 text-sm font-bold text-emerald-950">
            Download evidence PDF → Open Headrush → Upload your PDF → Confirm submission
          </p>
          {detail.verification?.teacher_feedback && (
            <p className="mt-3 rounded border border-emerald-200 bg-white px-3 py-2 text-sm text-emerald-950">
              Teacher feedback: {detail.verification.teacher_feedback}
            </p>
          )}
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded border border-emerald-200 bg-white p-4">
              <p className="text-sm font-bold text-emerald-950">1. Download evidence PDF</p>
              <p className="mt-1 text-sm text-slate-700">Save your individual assessment evidence document to your device.</p>
              <button type="button" className="mt-3 rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={() => { void handleAssessmentEvidenceDownload(); }} disabled={assessmentExporting}>
                {assessmentExporting ? 'Preparing PDF…' : 'Download evidence PDF'}
              </button>
            </div>
            <div className="rounded border border-emerald-200 bg-white p-4">
              <p className="text-sm font-bold text-emerald-950">2. Open Headrush</p>
              <p className="mt-1 text-sm text-slate-700">Open the correct {String(detail.assessment_stage || '').toUpperCase()} assessment card in Headrush.</p>
              <p className="mt-1 text-sm text-slate-700">Match the activity title on your PDF to your teacher’s assigned card. If the destination is unclear, ask before uploading.</p>
              {detail.external_next_url ? (
                <a className="mt-3 inline-flex rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white" href={detail.external_next_url} target="_blank" rel="noreferrer">Open Headrush</a>
              ) : (
                <p className="mt-3 text-sm font-semibold text-amber-800">Open Headrush using your normal school link or bookmark.</p>
              )}
            </div>
            <div className="rounded border border-emerald-200 bg-white p-4">
              <p className="text-sm font-bold text-emerald-950">3. Upload your PDF</p>
              <p className="mt-1 text-sm text-slate-700">In the Headrush assessment card, upload the evidence PDF you downloaded in Step 1 and finish the Headrush submission.</p>
              <p className="mt-2 text-sm text-slate-700">The PDF lists supporting files; it does not embed them. Upload any required code, photos, video, or other files separately. Complete any required demonstration with your teacher.</p>
            </div>
            <div className="rounded border border-emerald-200 bg-white p-4">
              <p className="text-sm font-bold text-emerald-950">4. Confirm submission</p>
              {detail.external_submission?.confirmed ? (
                <div className="mt-2 rounded bg-emerald-100 px-3 py-2 text-sm font-semibold text-emerald-900">
                  ✓ You confirmed this Headrush upload{detail.external_submission.confirmed_at ? ` on ${new Date(detail.external_submission.confirmed_at).toLocaleString()}` : ''}.
                </div>
              ) : (
                <>
                  <p className="mt-1 text-sm text-slate-700">Only confirm after the PDF has finished uploading in Headrush.</p>
                  <button type="button" className="mt-3 rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={() => { void handleConfirmExternalSubmission(); }} disabled={externalSubmissionConfirming}>
                    {externalSubmissionConfirming ? 'Confirming…' : 'I uploaded my PDF to Headrush'}
                  </button>
                </>
              )}
            </div>
          </div>
          <p className="mt-4 text-xs text-emerald-800">Headrush remains the official Level Up and standards grading record.</p>
          <p className="mt-1 text-xs text-emerald-800">Your confirmation records what you report. Hibbardsclass does not verify the upload in Headrush or assign Non Proficient, Proficient, or Mastery from completion alone.</p>
        </section>
      )}

      {assessmentDocumentNotice && (
        <div className="mt-4 rounded border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">{assessmentDocumentNotice}</div>
      )}

      {showCompletionReview && (
        <div className="mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-gray-900">Review missed questions</h2>
          <div className="mt-2 text-sm text-slate-700">
            {review && review.score_percent !== null
              ? `Score: ${review.correct_count}/${review.scorable_count} (${review.score_percent}%)`
              : `Scored items: ${review?.correct_count ?? 0}/${review?.scorable_count ?? 0}`}
          </div>
          {reviewMissedSteps.length === 0 ? (
            <div className="mt-3 text-sm text-emerald-700">No missed scored questions.</div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {reviewMissedSteps.map((item, index) => (
                  <button
                    key={`review-step-${item.step_id}`}
                    type="button"
                    className={`rounded border px-3 py-2 text-sm font-semibold ${
                      activeReviewStepId === item.step_id
                        ? 'border-rose-400 bg-rose-50 text-rose-900'
                        : 'border-slate-300 bg-white text-slate-700'
                    }`}
                    onClick={() => {
                      setActiveReviewStepId(item.step_id);
                      setActiveStepId(item.step_id);
                    }}
                  >
                    {index + 1}. Step {item.order}
                  </button>
                ))}
              </div>
              {activeReviewItem && (
                <div className="rounded border border-rose-100 bg-rose-50 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-rose-900">
                      Step {activeReviewItem.order}: {activeReviewItem.title}
                    </div>
                    <div className="text-xs text-rose-800">
                      Missed question {activeReviewIndex + 1} of {reviewMissedSteps.length}
                    </div>
                  </div>
                  <div className="mt-3 space-y-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-rose-700">Your answer</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-rose-900">
                        {formatStudentResponseText(activeReviewItem.student_response_text, activeReviewItem.student_response) || 'No saved response text.'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-rose-700">Correct answer</p>
                      <div className="mt-1 space-y-1">
                        {(Array.isArray(activeReviewItem.answer_key_lines) && activeReviewItem.answer_key_lines.length > 0
                          ? activeReviewItem.answer_key_lines
                          : (activeReviewItem.answer_key ? [JSON.stringify(activeReviewItem.answer_key)] : [])
                        ).map((line, idx) => (
                          <p key={`review-answer-${activeReviewItem.step_id}-${idx}`} className="whitespace-pre-wrap text-sm text-rose-900">
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                  {renderAiPanel(activeReviewItem.step_id, activeReviewItem.explanation_supported, activeReviewItem.ai_thread_preview)}
                  {reviewMissedSteps.length > 1 && (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
                        onClick={() => {
                          if (activeReviewIndex <= 0) return;
                          const nextItem = reviewMissedSteps[activeReviewIndex - 1];
                          setActiveReviewStepId(nextItem.step_id);
                          setActiveStepId(nextItem.step_id);
                        }}
                        disabled={activeReviewIndex <= 0}
                      >
                        Previous missed
                      </button>
                      <button
                        type="button"
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
                        onClick={() => {
                          if (activeReviewIndex < 0 || activeReviewIndex >= reviewMissedSteps.length - 1) return;
                          const nextItem = reviewMissedSteps[activeReviewIndex + 1];
                          setActiveReviewStepId(nextItem.step_id);
                          setActiveStepId(nextItem.step_id);
                        }}
                        disabled={activeReviewIndex < 0 || activeReviewIndex >= reviewMissedSteps.length - 1}
                      >
                        Next missed
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {studentStatus === 'completed' && detail.purpose !== 'assessment' && detail.external_next_url && (
        <div className="mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-gray-900">Next Assignment</h2>
          <p className="mt-2 text-sm text-slate-700">Continue to your next task:</p>
          <a
            href={detail.external_next_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center px-4 py-2 rounded bg-indigo-600 text-white text-sm font-semibold"
          >
            Open Next Assignment
          </a>
        </div>
      )}
    </div>
  );

  return (
    <OptionalAppShell
      enabled={redesignEnabled}
      role="student"
      navItems={studentV2NavItems('work', detail.section)}
      studentSectionId={detail.section}
      actions={shellActions}
      title={isPractice ? 'Independent work' : isAssessment ? 'Independent Assessment' : 'Make-up Assignment'}
    >
      {!isAssessment && <div className="mx-auto max-w-5xl space-y-3 p-4">
        <Link className="text-brand underline" to="/v2/student/units">Back to my unit work</Link>
        <p role="status">{draftRecovery.status}</p>
        {isPractice && studentStatus !== 'completed' && user && <UnitLiveInvitation assignment={detail.id} section={detail.section} attempt={detail.attempt_number || 1} owner={user.id} prepare={draftRecovery.prepareToLeave} />}
        {draftRecovery.error && <p role="alert">{draftRecovery.error}</p>}
        {draftRecovery.error && <button className="btn-secondary" onClick={draftRecovery.retry}>Retry recovery</button>}
        {draftRecovery.conflict !== null && <div className="space-y-2">
          <p>Compare copies before continuing. Your submitted responses are kept separately.</p>
          <label className="block">Recovery copy<textarea readOnly className="w-full" value={formatAssignmentRecovery(draftRecovery.recovery)} /></label>
          <label className="block">Saved copy<textarea readOnly className="w-full" value={formatAssignmentRecovery(draftRecovery.conflict)} /></label>
          <button className="btn-secondary" onClick={draftRecovery.useServerCopy}>Use saved version and keep recovery copy</button>
        </div>}
        {draftRecovery.backup && <details><summary>Previous recovery copy</summary><textarea className="w-full" readOnly value={formatAssignmentRecovery(draftRecovery.backup)} /></details>}
        {isPractice && studentStatus === 'completed' && <NextIndependentWork key={detail.id} sectionId={detail.section} />}
        {isPractice && studentStatus !== 'completed' && <button className="btn-primary" disabled={!draftRecovery.ready || replaySaving} onClick={async () => {
          try { await apiV2.post(`/assignments/${assignmentId}/finish-practice/`, { expected_owner_id: user?.id, attempt: detail.attempt_number || 1 }); await loadDetail(); }
          catch (error: any) { setReplayError(error?.response?.data?.error || 'Could not finish practice.'); }
        }}>Finish activity</button>}
        {isPractice && <p>Finish uses your saved responses. Draft edits are not submitted automatically. Finishing practice does not award proficiency; Headrush remains the official record.</p>}
      </div>}
      <fieldset disabled={!draftRecovery.ready} className="min-w-0">{replaySurface}</fieldset>
    </OptionalAppShell>
  );
};
